import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Calculator, BookOpen, Zap, Copy, Check, RotateCcw, ZoomIn, ZoomOut, Move, MousePointer, 
  X, Info, Eye, EyeOff, Filter, Settings, Search, Network, Play, Pause, 
  RotateCw, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Minimize2, Maximize2,
  Download, Upload, RefreshCw, Brain, Star, Heart, Hand, Move3D, ChevronDown as ChevronDownIcon,
  Wrench, Sparkles, Lightbulb, Code, Database, FileText
} from 'lucide-react';
import deepSeekThinkService from '../../services/deepseekThinkApi';
import ThinkProgressModal from '../ThinkProgressModal';
import { allSamples } from '../../data/knowledgeGraph';
import { renderMathToHTML } from '../../utils/mathRenderer';
import { formatMathExpression } from '../../utils/mathJaxUtils';
import { 
  getProblemsBySampleType,
  getProblemByNumber, 
  getFormattedProblemList, 
  getCurrentProblemNumber,
  getSampleInfo,
  supportsProblemDropdown,
  getDropdownTitle
} from '../../utils/universalProblemsList';
import GraphViewPage from './GraphViewPage';
import AnalysisViewPage from './AnalysisViewPage';
import ToolBoxPage from './ToolBoxPage';
import AIGeneratedPage from './AIGeneratedPage';

function MathsSolver({ problemType = 'quadratic', selectedSample = null, onSampleClear = null, onSampleUpdate = null }) {
  const [latexInput, setLatexInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [solution, setSolution] = useState(null);
  const [copied, setCopied] = useState(false);
  const [viewMode, setViewMode] = useState('graph');
  const [searchTerm, setSearchTerm] = useState('');
  const [workspaceData, setWorkspaceData] = useState([]);
  const [currentPage, setCurrentPage] = useState('solver'); // 'solver', 'toolbox', 'ai-generated'
  
  // Problem selector state
  const [selectedProblemNumber, setSelectedProblemNumber] = useState(null);
  const [isProblemDropdownOpen, setIsProblemDropdownOpen] = useState(false);
  
  // Think functionality state
  const [isThinking, setIsThinking] = useState(false);
  const [thinkProgress, setThinkProgress] = useState(null);
  const [showThinkProgress, setShowThinkProgress] = useState(false);

  // Enhanced JSON structure display state
  const [showJsonStructures, setShowJsonStructures] = useState(true);
  
  // Enhanced graph state with touch support
  const [graphState, setGraphState] = useState({
    zoom: 1,
    rotation: { x: 0, y: 0, z: 0 },
    translation: { x: 0, y: 0 },
    isDragging: false,
    dragStart: { x: 0, y: 0 },
    selectedNode: null,
    hoveredNode: null,
    visibleNodes: new Set(),
    highlightedPath: [],
    isAutoRotating: false,
    // Touch support for tablet
    touchStart: { x: 0, y: 0 },
    isTouching: false,
    lastTouchDistance: 0
  });

  // Safety check for graphState
  const getGraphStateValue = (key, defaultValue) => {
    if (!graphState || typeof graphState !== 'object') {
      return defaultValue;
    }
    return graphState[key] !== undefined ? graphState[key] : defaultValue;
  };

  // COMPLETELY FIXED: Ultra-stable floating window
  const [floatingWindow, setFloatingWindow] = useState({
    isVisible: false,
    x: 400,
    y: 200,
    width: 400,
    height: 760, // Changed from 380 to 760 (twice the height) as requested
    selectedEntity: null,
    isDragging: false,
    isResizing: false,
    dragStartX: 0,
    dragStartY: 0,
    startX: 0,
    startY: 0,
    startWidth: 0,
    startHeight: 0
  });

  // Safety check for floatingWindow
  const getFloatingWindowValue = (key, defaultValue) => {
    if (!floatingWindow || typeof floatingWindow !== 'object') {
      return defaultValue;
    }
    return floatingWindow[key] !== undefined ? floatingWindow[key] : defaultValue;
  };

  // Enhanced controls
  const [graphSettings, setGraphSettings] = useState({
    nodeSize: 0.6, // Changed from 1.2 to 0.6 as requested
    linkDistance: 120,
    showLabels: true,
    showRelationshipLabels: true,
    animate: true,
    layout: 'force',
    edgeThickness: 0.6 // Changed from 1.5 to 0.6 as requested
  });

  // Safety check for graphSettings
  const getGraphSetting = (key, defaultValue) => {
    if (!graphSettings || typeof graphSettings !== 'object') {
      return defaultValue;
    }
    return graphSettings[key] !== undefined ? graphSettings[key] : defaultValue;
  };

  // Advanced entity filters
  const [filterSettings, setFilterSettings] = useState({
    concepts: { visible: true, isolated: false },
    formulas: { visible: true, isolated: false },
    values: { visible: true, isolated: false },
    steps: { visible: true, isolated: false },
    relationships: { visible: true, isolated: false }
  });

  // Safety check for filterSettings
  const getFilterSetting = (type) => {
    if (!filterSettings || typeof filterSettings !== 'object') {
      return { visible: true, isolated: false };
    }
    return filterSettings[type] || { visible: true, isolated: false };
  };

  const svgRef = useRef(null);
  const floatingRef = useRef(null);
  const autoRotateRef = useRef(null);
  const dragStateRef = useRef({ isDragging: false, isResizing: false });
  const fileInputRef = useRef(null);

  // Handle selected sample
  useEffect(() => {
    if (selectedSample) {
      // Set the input to the sample problem
      setLatexInput(selectedSample.problem);
      
      // Create solution from sample data
      const sampleSolution = {
        equation: selectedSample.problem,
        problemType: selectedSample.problemType,
        graphData: selectedSample.graphData,
        solution: selectedSample.problem,
        description: selectedSample.description,
        difficulty: selectedSample.difficulty
      };
      
      setSolution(sampleSolution);
      setIsLoading(false);
      
      // Set the current problem number for the dropdown
      const currentNumber = getCurrentProblemNumber(selectedSample);
      setSelectedProblemNumber(currentNumber);
    }
  }, [selectedSample]);

  // JSON import handlers
  const handleImportClick = useCallback(() => {
    if (fileInputRef && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleFileChange = useCallback(
    (event) => {
      try {
        const file = event?.target?.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const text = reader.result || '';
            const data = JSON.parse(text);

            // Update input if present
            if (data.problem) {
              setLatexInput(String(data.problem));
            }

            // Build a solution-like object from imported JSON
            const importedSolution = {
              equation: data.problem || '',
              problemType: data.problemType || 'quadratic',
              graphData: data.graphData || null,
              solution: data.solution || data.problem || '',
              description: data.description || 'Imported problem',
              difficulty: data.difficulty || 'intermediate'
            };

            setSolution(importedSolution);
            setShowJsonStructures(true);

            // Notify parent if available
            if (onSampleUpdate) {
              try {
                onSampleUpdate({
                  id: data.id || 'imported',
                  problem: importedSolution.equation,
                  description: importedSolution.description,
                  difficulty: importedSolution.difficulty,
                  problemType: importedSolution.problemType,
                  graphData: importedSolution.graphData
                });
              } catch (_) {
                // no-op if parent does not expect this shape
              }
            }
          } catch (err) {
            console.error('Failed to parse imported JSON:', err);
            alert('Import failed: invalid JSON content.');
          }
        };
        reader.readAsText(file);
      } finally {
        // reset input so same file can be selected again
        if (event?.target) event.target.value = '';
      }
    },
    [onSampleUpdate]
  );

  // Build export payload of current knowledge graph
  const buildKnowledgeGraphExport = useCallback(() => {
    if (solution?.graphData) {
      return {
        problem: solution.equation || latexInput || '',
        problemType: solution.problemType || problemType,
        analysis: solution.description || '',
        graphData: solution.graphData
      };
    }
    // Fallback to dynamic JSON structure for current selection
    try {
      return getDynamicJsonStructure();
    } catch (_) {
      return { problem: latexInput || '', problemType, graphData: { nodes: [], links: [] } };
    }
  }, [solution, latexInput, problemType]);

  const sanitizeFilename = (name) => {
    if (!name) return 'KG JSON Untitled';
    return `KG JSON ${String(name)}`.replace(/[/\\?%*:|"<>]/g, '_');
  };

  const handleDownloadJson = useCallback(() => {
    try {
      const data = buildKnowledgeGraphExport();
      const title = (typeof getCurrentProblemInfo === 'function' ? getCurrentProblemInfo()?.name : '') || 'Untitled';
      const filename = `${sanitizeFilename(title)}.json`;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download JSON:', error);
      alert('Download failed.');
    }
  }, [buildKnowledgeGraphExport]);

  // Handle problem selection from dropdown
  const handleProblemSelection = (problemNumber) => {
    const { sampleType, year } = getSampleInfo(selectedSample);
    const selectedProblem = getProblemByNumber(sampleType, problemNumber, year);
    if (selectedProblem) {
      setLatexInput(selectedProblem.problem);
      setSelectedProblemNumber(problemNumber);
      setIsProblemDropdownOpen(false);
      
      // Create solution from selected problem data
      const problemSolution = {
        equation: selectedProblem.problem,
        problemType: selectedProblem.problemType,
        graphData: selectedProblem.graphData,
        solution: selectedProblem.problem,
        description: selectedProblem.description,
        difficulty: selectedProblem.difficulty
      };
      
      setSolution(problemSolution);
      setIsLoading(false);
      
      // Update the selectedSample in the parent component
      if (onSampleUpdate) {
        onSampleUpdate(selectedProblem);
      }
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isProblemDropdownOpen && !event.target.closest('.problem-dropdown')) {
        setIsProblemDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProblemDropdownOpen]);

  // Handle ToolBox run button
  const handleToolBoxRun = (workspace) => {
    setWorkspaceData(workspace);
    setCurrentPage('ai-generated');
  };

  const handleGraphViewClick = () => {
    setCurrentPage('graph-view');
  };

  const handleAnalysisViewClick = () => {
    setCurrentPage('analysis-view');
  };

  const handleToolBoxClick = () => {
    setCurrentPage('toolbox');
  };

  const handleAIGeneratedClick = () => {
    setCurrentPage('ai-generated');
  };

  const handleBackToSolver = () => {
    setCurrentPage('solver');
  };

  // Think functionality
  const handleThinkAboutProblem = async () => {
    // Validate input
    if (!latexInput || latexInput.trim() === '') {
      alert('Please enter a mathematical problem to analyze.');
      return;
    }
    
    // Check connection status
    if (connectionStatus === 'failed') {
      const retry = window.confirm('DeepSeek API connection failed. Would you like to retry the connection before proceeding?');
      if (retry) {
        setConnectionStatus('testing');
        try {
          const result = await deepSeekThinkService.testConnection();
          setConnectionStatus(result.success ? 'connected' : 'failed');
          if (!result.success) {
            window.alert('Connection still failed. Please check your internet connection and try again.');
            return;
          }
        } catch (error) {
          setConnectionStatus('failed');
          window.alert('Connection failed. Please check your internet connection and try again.');
          return;
        }
      } else {
        return;
      }
    }
    
    if (isThinking) return;
    
    try {
      setIsThinking(true);
      setShowThinkProgress(true);
      setThinkProgress(null);
      
      console.log('Starting "Let\'s think" analysis with input:', latexInput);
      console.log('Current problem type:', getCurrentProblemType());
      console.log('Is HKDSE problem:', isHKDSEProblem());
      
      // Get the appropriate JSON structure for the current problem type
      const currentJsonStructure = getDynamicJsonStructure();
      console.log('Using dynamic JSON structure for:', getCurrentProblemType());
      console.log('Selected sample:', selectedSample);
      
               // Add a timeout wrapper with longer timeout to match API service
         const timeoutDuration = latexInput.length > 500 ? 180000 : 150000; // Extended timeout: 150s for simple, 180s for complex problems
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timed out. Please try again.')), timeoutDuration);
      });
      
      const result = await Promise.race([
        deepSeekThinkService.thinkAboutProblem(
          latexInput.trim(), 
          problemType, 
          (progress) => {
            setThinkProgress(progress);
          },
          currentJsonStructure // Pass the JSON structure to the API
        ),
        timeoutPromise
      ]);
      
      console.log('Think analysis completed:', result);
      
      // Create solution from think result
      const thinkSolution = {
        equation: result.problem || latexInput,
        problemType: result.problemType || problemType,
        graphData: result.graphData,
        solution: result.problem || latexInput,
        description: result.analysis || `AI analysis of ${result.problemType || problemType} problem`,
        difficulty: "intermediate",
        metadata: result.metadata,
        source: 'ai_think', // Mark this as AI-generated
        problemTypeCategory: getCurrentProblemType() // Add the problem type category
      };
      
      setSolution(thinkSolution);
      setIsThinking(false);
      
      // Keep progress modal open for a moment to show completion
      setTimeout(() => {
        setShowThinkProgress(false);
        setThinkProgress(null);
      }, 2000);
      
    } catch (error) {
      console.error('Error in thinkAboutProblem:', error);
      setIsThinking(false);
      setShowThinkProgress(false);
      setThinkProgress(null);
      
      // Show user-friendly error message
      let errorMessage = error.message;
      if (error.message.includes('timeout')) {
        errorMessage = 'The AI analysis is taking too long. Please try:\n1. A simpler mathematical problem\n2. Check your internet connection\n3. Try again in a few moments';
      } else if (error.message.includes('JSON')) {
        errorMessage = 'The AI response format was invalid. A basic analysis has been created instead.';
      } else if (error.message.includes('AbortError')) {
        errorMessage = 'The request was cancelled due to timeout. Please try a simpler problem or check your connection.';
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (error.message.includes('connection failed') || error.message.includes('DeepSeek API connection')) {
        errorMessage = 'DeepSeek API connection failed. Please check your internet connection and try again.';
      }
      
      // Don't show alert for JSON parsing errors since we have a fallback
      if (!error.message.includes('JSON')) {
        alert(`Analysis failed: ${errorMessage}`);
      }
    }
  };

  // Sample equations based on problem type
  const getSampleEquations = (type) => {
    switch (type) {
      case 'quadratic':
        return [
          'x^2 + 5x + 6 = 0',
          '2x^2 - 4x - 6 = 0',
          'x^2 - 9 = 0',
          '3x^2 + 2x - 1 = 0',
          'x^2 + 4x + 4 = 0'
        ];
      case 'circle_geometry':
        return [
          'Find equation of circle with center (3, -2) and radius 5',
          'Find center and radius of circle x^2 + y^2 - 6x + 4y - 12 = 0',
          'Find equation of circle passing through (1,2), (3,4), (5,6)',
          'Find area of circle with radius 7',
          'Find circumference of circle with diameter 10'
        ];
      case 'factor_remainder':
        return [
          'Factor x^3 - 8',
          'Find remainder when x^3 + 2x^2 - 5x + 1 is divided by x - 2',
          'Factor x^4 - 16',
          'Find remainder when 2x^3 - 3x^2 + 4x - 5 is divided by x + 1',
          'Factor x^2 - 5x + 6'
        ];
      case 'probability':
        return [
          'Find probability of rolling a 6 on a fair die',
          'Find probability of drawing 2 aces from a deck of 52 cards',
          'Find probability of getting exactly 3 heads in 5 coin flips',
          'Find probability of selecting 2 red balls from 5 red and 3 blue balls',
          'Find probability of getting sum of 7 when rolling 2 dice'
        ];
      case 'statistics':
        return [
          'Find mean of numbers: 2, 4, 6, 8, 10',
          'Find median of numbers: 1, 3, 5, 7, 9, 11',
          'Find standard deviation of: 2, 4, 6, 8, 10',
          'Find variance of: 1, 2, 3, 4, 5',
          'Find mode of: 1, 2, 2, 3, 4, 4, 5'
        ];
      case 'trigonometry':
        return [
          'Find sin(30°)',
          'Find cos(60°)',
          'Find tan(45°)',
          'Find sin(π/6)',
          'Find cos(π/3)'
        ];
      default:
        return [
          'x^2 + 5x + 6 = 0',
          '2x^2 - 4x - 6 = 0',
          'x^2 - 9 = 0',
          '3x^2 + 2x - 1 = 0',
          'x^2 + 4x + 4 = 0'
        ];
    }
  };

  const sampleEquations = getSampleEquations(problemType);

  // Auto-rotation effect
  useEffect(() => {
    if (graphState.isAutoRotating) {
      autoRotateRef.current = setInterval(() => {
        try {
          setGraphState(prev => ({
            ...prev,
            rotation: {
              ...prev.rotation,
              z: (prev.rotation.z + 1) % 360
            }
          }));
        } catch (error) {
          console.error('MathsSolver: Error in auto-rotation', error);
        }
      }, 50);
    } else {
      if (autoRotateRef.current) {
        clearInterval(autoRotateRef.current);
        autoRotateRef.current = null;
      }
    }

    return () => {
      if (autoRotateRef.current) {
        clearInterval(autoRotateRef.current);
      }
    };
  }, [graphState.isAutoRotating]);

  // Initialize visible nodes when solution changes
  useEffect(() => {
    if (solution?.graphData?.nodes) {
      try {
        const allNodes = new Set(solution.graphData.nodes.map(n => n.id).filter(Boolean));
        setGraphState(prev => ({ ...prev, visibleNodes: allNodes }));
      } catch (error) {
        console.error('MathsSolver: Error initializing visible nodes', error);
        setGraphState(prev => ({ ...prev, visibleNodes: new Set() }));
      }
    }
  }, [solution]);

  // Enhanced AI solver with detailed graph data for different problem types
  const solveProblem = async (problem) => {
    try {
      console.log('MathsSolver: solveProblem called', { problem, problemType });
      
      if (!problem || typeof problem !== 'string') {
        console.log('MathsSolver: Invalid problem parameter', { problem });
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      console.log('MathsSolver: Loading started');
      
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Get the appropriate sample data based on problem type
      const sampleDataKey = problemType === 'quadratic' ? 'quadratic' : 
                           problemType === 'circle_geometry' ? 'circleGeometry' :
                           problemType === 'factor_remainder' ? 'factorRemainder' :
                           problemType === 'probability' ? 'probability' :
                           problemType === 'statistics' ? 'statistics' :
                           problemType === 'trigonometry' ? 'trigonometry' : 'quadratic';
      
      console.log('MathsSolver: Sample data key', { sampleDataKey, allSamples: Object.keys(allSamples) });
      
      const sampleData = allSamples[sampleDataKey];
      console.log('MathsSolver: Sample data found', { sampleData, hasFundamental: sampleData?.fundamental?.length > 0 });
    
      if (sampleData && sampleData.fundamental && sampleData.fundamental.length > 0) {
        // Use the first fundamental sample as the base data
        const baseSample = sampleData.fundamental[0];
        console.log('MathsSolver: Using base sample', { baseSample });
        
        // Validate sample data before setting solution
        if (!baseSample.graphData || !Array.isArray(baseSample.graphData.nodes) || !Array.isArray(baseSample.graphData.links)) {
          console.error('MathsSolver: Invalid sample data structure', { baseSample, sampleDataKey });
          setSolution(null);
          setIsLoading(false);
          return;
        }
        
        // Create dynamic solution data based on the actual problem
        const dynamicGraphData = {
          nodes: [
            {
              id: "problem",
              name: "Problem Statement",
              type: "concepts",
              description: "The mathematical problem to be solved",
              value: problem,
              importance: 10,
              x: 0, y: -200, z: 0
            },
            {
              id: "problem_type",
              name: "Problem Type",
              type: "concepts",
              description: "Classification of the mathematical problem",
              value: problemType,
              importance: 8,
              x: -150, y: -100, z: 20
            },
            {
              id: "analysis",
              name: "Problem Analysis",
              type: "concepts",
              description: "Detailed analysis of the mathematical problem",
              value: `Analysis of ${problemType} problem: ${problem}`,
              importance: 9,
              x: 150, y: -100, z: 20
            },
            {
              id: "solution_method",
              name: "Solution Method",
              type: "formulas",
              description: "Mathematical method to solve this problem",
              value: `Use appropriate ${problemType} solving method`,
              importance: 9,
              x: -120, y: 100, z: -15
            },
            {
              id: "step1",
              name: "Step 1: Understand Problem",
              type: "steps",
              description: "First step in solving the problem",
              value: "Identify the problem type and key variables",
              importance: 8,
              x: 120, y: 100, z: -15
            },
            {
              id: "step2",
              name: "Step 2: Apply Method",
              type: "steps",
              description: "Second step in solving the problem",
              value: "Apply the appropriate mathematical method",
              importance: 9,
              x: 0, y: 50, z: 40
            },
            {
              id: "step3",
              name: "Step 3: Calculate Result",
              type: "steps",
              description: "Third step in solving the problem",
              value: "Perform calculations to find the solution",
              importance: 9,
              x: -250, y: -50, z: 0
            },
            {
              id: "final_answer",
              name: "Final Answer",
              type: "values",
              description: "The solution to the mathematical problem",
              value: "Solution will be calculated based on the problem",
              importance: 10,
              x: 250, y: -50, z: 0
            }
          ],
          links: [
            { source: "problem", target: "problem_type", type: "classification" },
            { source: "problem", target: "analysis", type: "analysis" },
            { source: "analysis", target: "solution_method", type: "method_selection" },
            { source: "solution_method", target: "step1", type: "implementation" },
            { source: "step1", target: "step2", type: "progression" },
            { source: "step2", target: "step3", type: "progression" },
            { source: "step3", target: "final_answer", type: "result" }
          ]
        };

        const result = {
          equation: problem,
          problemType: problemType,
          graphData: dynamicGraphData,
          solution: problem,
          description: `Dynamic analysis of ${problemType} problem: ${problem}`,
          difficulty: "intermediate"
        };
        
        console.log('MathsSolver: Setting solution from dynamic data', {
          sampleDataKey,
          result,
          graphData: dynamicGraphData,
          nodesCount: dynamicGraphData?.nodes?.length,
          linksCount: dynamicGraphData?.links?.length
        });
        
        setSolution(result);
      } else {
        console.log('MathsSolver: Using fallback quadratic solving');
        // Fallback to quadratic solving for unknown problem types
        const cleanEq = problem.replace(/\s/g, '').toLowerCase();
        
        let a = 1, b = 0, c = 0;
        
        if (cleanEq.includes('x^2')) {
          const aMatch = cleanEq.match(/([+-]?\d*)x\^2/);
          if (aMatch) {
            a = aMatch[1] === '' || aMatch[1] === '+' ? 1 : 
                aMatch[1] === '-' ? -1 : 
                parseInt(aMatch[1]);
          }
        }
        
        const bMatch = cleanEq.match(/([+-]?\d*)x(?!\^)/);
        if (bMatch) {
          b = bMatch[1] === '' || bMatch[1] === '+' ? 1 : 
              bMatch[1] === '-' ? -1 : 
              parseInt(bMatch[1]);
        }
        
        const cMatch = cleanEq.match(/([+-]?\d+)(?!.*x)/);
        if (cMatch) {
          c = parseInt(cMatch[1]);
        }
        
        const discriminant = b * b - 4 * a * c;
        let x1, x2, nature;
        
        if (discriminant > 0) {
          x1 = (-b + Math.sqrt(discriminant)) / (2 * a);
          x2 = (-b - Math.sqrt(discriminant)) / (2 * a);
          nature = "Two real and distinct roots";
        } else if (discriminant === 0) {
          x1 = x2 = -b / (2 * a);
          nature = "One real root (repeated)";
        } else {
          const realPart = -b / (2 * a);
          const imagPart = Math.sqrt(-discriminant) / (2 * a);
          x1 = `${realPart.toFixed(2)} + ${imagPart.toFixed(2)}i`;
          x2 = `${realPart.toFixed(2)} - ${imagPart.toFixed(2)}i`;
          nature = "Two complex roots";
        }
        
        const graphData = createProfessionalGraphData(problem, a, b, c, discriminant, x1, x2, nature);
        
        // Validate the created graph data
        if (!graphData || !Array.isArray(graphData.nodes) || !Array.isArray(graphData.links)) {
          console.error('MathsSolver: Invalid graph data created', { graphData });
          setSolution(null);
          setIsLoading(false);
          return;
        }
        
        const result = {
          equation: problem,
          problemType: 'quadratic',
          coefficients: { a, b, c },
          discriminant,
          solutions: { x1, x2 },
          nature,
          graphData: graphData
        };
        
        console.log('MathsSolver: Setting solution from fallback', {
          result,
          graphData: result.graphData,
          nodesCount: result.graphData?.nodes?.length,
          linksCount: result.graphData?.links?.length
        });
        
        setSolution(result);
      }
      
      console.log('MathsSolver: Loading finished');
      setIsLoading(false);
    } catch (error) {
      console.error('MathsSolver: Error in solveProblem', error);
      setIsLoading(false);
      setSolution(null);
    }
  };

  const createProfessionalGraphData = (equation, a, b, c, discriminant, x1, x2, nature) => {
    const entities = {
      concepts: [
        { 
          id: 'equation', 
          name: 'Quadratic Equation', 
          type: 'concepts', 
          description: 'A polynomial equation of degree 2 in standard form',
          value: equation, 
          importance: 10,
          x: 0, y: -220, z: 0
        },
        { 
          id: 'standard_form', 
          name: 'Standard Form', 
          type: 'concepts', 
          description: 'The general representation ax² + bx + c = 0',
          value: 'ax² + bx + c = 0', 
          importance: 9,
          x: -180, y: -120, z: 20
        },
        { 
          id: 'nature', 
          name: 'Nature of Roots', 
          type: 'concepts', 
          description: 'Classification of solutions based on discriminant',
          value: nature, 
          importance: 8,
          x: 200, y: 0, z: -10
        },
        { 
          id: 'parabola', 
          name: 'Parabola', 
          type: 'concepts', 
          description: 'Graphical representation of the quadratic function',
          value: `Opens ${a > 0 ? 'upward' : 'downward'}`, 
          importance: 7,
          x: 250, y: 120, z: 15
        }
      ],
      formulas: [
        { 
          id: 'discriminant', 
          name: 'Discriminant', 
          type: 'formulas', 
          description: 'The expression b² - 4ac that determines root nature',
          value: `Δ = ${discriminant}`, 
          importance: 9,
          x: -200, y: 0, z: 30
        },
        { 
          id: 'quadratic_formula', 
          name: 'Quadratic Formula', 
          type: 'formulas', 
          description: 'Universal method: x = (-b ± √Δ) / (2a)',
          value: 'x = (-b ± √Δ) / (2a)', 
          importance: 10,
          x: 0, y: 0, z: 25
        },
        { 
          id: 'vertex_formula', 
          name: 'Vertex Formula', 
          type: 'formulas', 
          description: 'Vertex coordinates for the parabola',
          value: `h = -b/(2a) = ${(-b/(2*a)).toFixed(3)}`, 
          importance: 6,
          x: 180, y: -120, z: -20
        }
      ],
      values: [
        { 
          id: 'coefficients', 
          name: 'Coefficients', 
          type: 'values', 
          description: 'Numerical parameters defining the equation',
          value: `a=${a}, b=${b}, c=${c}`, 
          importance: 8,
          x: 180, y: -180, z: 10
        },
        { 
          id: 'solution1', 
          name: 'Root 1', 
          type: 'values', 
          description: 'First solution of the equation',
          value: `x₁ = ${typeof x1 === 'number' ? x1.toFixed(4) : x1}`, 
          importance: 9,
          x: -120, y: 140, z: -15
        },
        { 
          id: 'solution2', 
          name: 'Root 2', 
          type: 'values', 
          description: 'Second solution of the equation',
          value: `x₂ = ${typeof x2 === 'number' ? x2.toFixed(4) : x2}`, 
          importance: 9,
          x: 120, y: 140, z: -15
        },
        { 
          id: 'discriminant_value', 
          name: 'Discriminant Value', 
          type: 'values', 
          description: 'Numerical value determining root characteristics',
          value: `Δ = ${discriminant}`, 
          importance: 8,
          x: -120, y: 60, z: 35
        }
      ],
      steps: [
        { 
          id: 'identify', 
          name: 'Identify Coefficients', 
          type: 'steps', 
          description: 'Extract a, b, c from the equation',
          value: 'Step 1: Extract coefficients', 
          importance: 7,
          x: -250, y: -60, z: 0
        },
        { 
          id: 'calculate', 
          name: 'Calculate Discriminant', 
          type: 'steps', 
          description: 'Compute b² - 4ac to determine solution type',
          value: 'Step 2: Compute Δ', 
          importance: 8,
          x: -80, y: -20, z: 40
        },
        { 
          id: 'apply', 
          name: 'Apply Formula', 
          type: 'steps', 
          description: 'Use quadratic formula to find roots',
          value: 'Step 3: Apply formula', 
          importance: 9,
          x: 80, y: 60, z: 20
        },
        { 
          id: 'verify', 
          name: 'Verify Solutions', 
          type: 'steps', 
          description: 'Substitute back to confirm results',
          value: 'Step 4: Verification', 
          importance: 7,
          x: 0, y: 200, z: 0
        }
      ]
    };

    const relationships = [
      { from: 'equation', to: 'standard_form', type: 'represents as', strength: 9 },
      { from: 'equation', to: 'coefficients', type: 'contains', strength: 10 },
      { from: 'standard_form', to: 'identify', type: 'guides', strength: 8 },
      { from: 'coefficients', to: 'discriminant', type: 'calculates', strength: 9 },
      { from: 'coefficients', to: 'vertex_formula', type: 'determines', strength: 7 },
      { from: 'identify', to: 'calculate', type: 'leads to', strength: 8 },
      { from: 'discriminant', to: 'discriminant_value', type: 'evaluates to', strength: 10 },
      { from: 'discriminant_value', to: 'nature', type: 'determines', strength: 9 },
      { from: 'calculate', to: 'apply', type: 'enables', strength: 9 },
      { from: 'quadratic_formula', to: 'apply', type: 'implemented in', strength: 10 },
      { from: 'quadratic_formula', to: 'solution1', type: 'yields', strength: 10 },
      { from: 'quadratic_formula', to: 'solution2', type: 'yields', strength: 10 },
      { from: 'apply', to: 'verify', type: 'requires', strength: 8 },
      { from: 'solution1', to: 'verify', type: 'needs', strength: 8 },
      { from: 'solution2', to: 'verify', type: 'needs', strength: 8 },
      { from: 'nature', to: 'parabola', type: 'describes', strength: 7 },
      { from: 'coefficients', to: 'parabola', type: 'shapes', strength: 8 },
      { from: 'solution1', to: 'parabola', type: 'intersects at', strength: 9 },
      { from: 'solution2', to: 'parabola', type: 'intersects at', strength: 9 }
    ];

    const allEntities = [...entities.concepts, ...entities.formulas, ...entities.values, ...entities.steps];
    
    const nodes = allEntities.map(entity => ({
      ...entity,
      radius: Math.max(8, entity.importance * 2.5)
    }));

    const links = relationships.map(rel => ({
      ...rel,
      source: rel.from,
      target: rel.to,
      width: Math.max(1.5, rel.strength / 3)
    }));

    const result = { nodes, links };
    
    // Validate the result before returning
    if (!Array.isArray(result.nodes) || !Array.isArray(result.links)) {
      console.error('MathsSolver: Invalid result from createProfessionalGraphData', { result });
      return { nodes: [], links: [] };
    }
    
    // Validate that all nodes have required properties
    const invalidNodes = result.nodes.filter(node => 
      !node.id || typeof node.x !== 'number' || typeof node.y !== 'number' ||
      isNaN(node.x) || isNaN(node.y) || !isFinite(node.x) || !isFinite(node.y)
    );
    
    if (invalidNodes.length > 0) {
      console.error('MathsSolver: Invalid nodes in createProfessionalGraphData result', { invalidNodes });
      // Fix invalid nodes
      result.nodes = result.nodes.map(node => {
        if (!node.id || typeof node.x !== 'number' || typeof node.y !== 'number' ||
            isNaN(node.x) || isNaN(node.y) || !isFinite(node.x) || !isFinite(node.y)) {
          return {
            ...node,
            id: node.id || 'node_' + Math.random(),
            x: 0,
            y: 0,
            z: 0
          };
        }
        return node;
      });
    }
    
    console.log('MathsSolver: createProfessionalGraphData result', {
      nodesCount: nodes.length,
      linksCount: links.length,
      firstNode: nodes[0],
      firstLink: links[0],
      result
    });
    
    return result;
  };

  // Enhanced 3D transformation with bulletproof error handling
  const apply3DTransform = useCallback((x, y, z = 0) => {
    // Comprehensive safety check for input parameters
    if (typeof x !== 'number' || typeof y !== 'number' || typeof z !== 'number' ||
        isNaN(x) || isNaN(y) || isNaN(z) ||
        !isFinite(x) || !isFinite(y) || !isFinite(z)) {
      console.log('MathsSolver: Invalid input coordinates', { x, y, z, types: { x: typeof x, y: typeof y, z: typeof z } });
      return { 
        x: 0, 
        y: 0, 
        scale: 1,
        depth: 0 
      };
    }

    try {
      const rotation = getGraphStateValue('rotation', { x: 0, y: 0, z: 0 });
      const radX = (rotation.x * Math.PI) / 180;
      const radY = (rotation.y * Math.PI) / 180;
      const radZ = (rotation.z * Math.PI) / 180;

      let newX = x * Math.cos(radY) - z * Math.sin(radY);
      let newZ = x * Math.sin(radY) + z * Math.cos(radY);
      
      let newY = y * Math.cos(radX) - newZ * Math.sin(radX);
      newZ = y * Math.sin(radX) + newZ * Math.cos(radX);
      
      const finalX = newX * Math.cos(radZ) - newY * Math.sin(radZ);
      const finalY = newX * Math.sin(radZ) + newY * Math.cos(radZ);

      const perspective = 800;
      const projectedX = (finalX * perspective) / (perspective + newZ);
      const projectedY = (finalY * perspective) / (perspective + newZ);
      const scale = perspective / (perspective + newZ);

      // Comprehensive safety check for output values
      const result = { 
        x: Number.isFinite(projectedX) ? projectedX : 0, 
        y: Number.isFinite(projectedY) ? projectedY : 0, 
        scale: Number.isFinite(scale) ? Math.max(0.3, Math.min(2, scale)) : 1,
        depth: Number.isFinite(newZ) ? newZ : 0 
      };

      // Final validation
      if (!Number.isFinite(result.x) || !Number.isFinite(result.y) || 
          !Number.isFinite(result.scale) || !Number.isFinite(result.depth)) {
        console.log('MathsSolver: Invalid output coordinates', { result, input: { x, y, z } });
        return { x: 0, y: 0, scale: 1, depth: 0 };
      }

      return result;
    } catch (error) {
      console.error('MathsSolver: Error in apply3DTransform', error, { x, y, z });
      return { x: 0, y: 0, scale: 1, depth: 0 };
    }
  }, [graphState.rotation]);

  // FIXED: Entity click handler
  const handleEntityClick = useCallback((entity, event) => {
    if (!entity || !entity.id) {
      console.error('MathsSolver: Invalid entity in handleEntityClick', { entity });
      return;
    }
    
    if (event && typeof event.stopPropagation === 'function') {
      event.stopPropagation();
    }
    
    try {
      setGraphState(prev => ({ 
        ...prev, 
        selectedNode: entity.id,
        highlightedPath: findConnectedNodes(entity.id, solution?.graphData)
      }));
      
      setFloatingWindow(prev => ({
        ...prev,
        isVisible: true,
        selectedEntity: entity
      }));
    } catch (error) {
      console.error('MathsSolver: Error in handleEntityClick', error);
    }
  }, [solution]);

  const handleEntityHover = useCallback((entity) => {
    try {
      const entityId = entity && entity.id ? entity.id : null;
      setGraphState(prev => ({ ...prev, hoveredNode: entityId }));
    } catch (error) {
      console.error('MathsSolver: Error in handleEntityHover', error);
    }
  }, []);

  const findConnectedNodes = (nodeId, graphData) => {
    // Safety check for valid parameters
    if (!nodeId || !graphData || !graphData.links) {
      return [];
    }
    
    try {
      const connected = new Set([nodeId]);
      graphData.links.forEach(link => {
        // Safety check for link object
        if (!link || typeof link !== 'object') {
          return;
        }
        
        const sourceId = link.source || link.from;
        const targetId = link.target || link.to;
        
        // Safety check for source and target IDs
        if (sourceId === nodeId && targetId) connected.add(targetId);
        if (targetId === nodeId && sourceId) connected.add(sourceId);
      });
      return Array.from(connected);
    } catch (error) {
      console.error('MathsSolver: Error finding connected nodes', error);
      return [];
    }
  };

  // COMPLETELY FIXED: Ultra-stable drag handlers with ref-based state and touch support
  const handleFloatingMouseDown = useCallback((e) => {
    if (!e || typeof e.preventDefault !== 'function' || typeof e.stopPropagation !== 'function') {
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    
    // Check if the click is on a button (like close button) - don't start dragging
    if (e.target && e.target.closest && e.target.closest('button')) {
      return;
    }
    
    const isResizeHandle = e.target && e.target.closest ? e.target.closest('.resize-handle') : null;
    const isHeader = e.target && e.target.closest ? e.target.closest('.window-header') : null;
    
    if (!isResizeHandle && !isHeader) return;

    const startX = e.clientX || e.touches?.[0]?.clientX || 0;
    const startY = e.clientY || e.touches?.[0]?.clientY || 0;
    
    if (isResizeHandle) {
      dragStateRef.current = { isDragging: false, isResizing: true };
      setFloatingWindow(prev => ({
        ...prev,
        isResizing: true,
        dragStartX: startX,
        dragStartY: startY,
        startWidth: prev.width,
        startHeight: prev.height
      }));
    } else if (isHeader) {
      dragStateRef.current = { isDragging: true, isResizing: false };
      setFloatingWindow(prev => ({
        ...prev,
        isDragging: true,
        dragStartX: startX,
        dragStartY: startY,
        startX: prev.x,
        startY: prev.y
      }));
    }

    // Set body cursor immediately
    if (document && document.body) {
      document.body.style.cursor = isResizeHandle ? 'se-resize' : 'grabbing';
      document.body.style.userSelect = 'none';
      document.body.style.touchAction = 'none'; // Prevent scrolling on touch devices
    }
  }, []);

  // FIXED: Single global mouse move handler with touch support
  const handleGlobalMouseMove = useCallback((e) => {
    const { isDragging, isResizing } = dragStateRef.current;
    
    if (!isDragging && !isResizing) return;

    e.preventDefault();

    // Handle both mouse and touch events
    const clientX = e.clientX || e.touches?.[0]?.clientX || 0;
    const clientY = e.clientY || e.touches?.[0]?.clientY || 0;

    if (isDragging) {
      const dragStartX = getFloatingWindowValue('dragStartX', 0);
      const dragStartY = getFloatingWindowValue('dragStartY', 0);
      const startX = getFloatingWindowValue('startX', 0);
      const startY = getFloatingWindowValue('startY', 0);
      const width = getFloatingWindowValue('width', 400);
      const height = getFloatingWindowValue('height', 760);
      
      const deltaX = clientX - dragStartX;
      const deltaY = clientY - dragStartY;
      
      const newX = Math.max(0, Math.min(window.innerWidth - width, startX + deltaX));
      const newY = Math.max(0, Math.min(window.innerHeight - height, startY + deltaY));
      
      setFloatingWindow(prev => ({
        ...prev,
        x: newX,
        y: newY
      }));
    } else if (isResizing) {
      const dragStartX = getFloatingWindowValue('dragStartX', 0);
      const dragStartY = getFloatingWindowValue('dragStartY', 0);
      const startWidth = getFloatingWindowValue('startWidth', 400);
      const startHeight = getFloatingWindowValue('startHeight', 760);
      
      const deltaX = clientX - dragStartX;
      const deltaY = clientY - dragStartY;
      
      const newWidth = Math.max(300, Math.min(800, startWidth + deltaX));
      const newHeight = Math.max(500, Math.min(1200, startHeight + deltaY)); // Adjusted for larger default height
      
      setFloatingWindow(prev => ({
        ...prev,
        width: newWidth,
        height: newHeight
      }));
    }
  }, []);

  // FIXED: Single global mouse up handler with touch support
  const handleGlobalMouseUp = useCallback(() => {
    dragStateRef.current = { isDragging: false, isResizing: false };
    
    setFloatingWindow(prev => ({
      ...prev,
      isDragging: false,
      isResizing: false
    }));

    // Reset body styles
    if (document && document.body) {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.touchAction = ''; // Restore touch action
    }
  }, []);

  // FIXED: Single effect for global event listeners
  useEffect(() => {
    const { isDragging, isResizing } = dragStateRef.current;
    
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleGlobalMouseMove, { passive: false });
      document.addEventListener('mouseup', handleGlobalMouseUp, { passive: false });
      
      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [handleGlobalMouseMove, handleGlobalMouseUp, floatingWindow.isDragging, floatingWindow.isResizing]);

  // Graph interaction handlers with enhanced touch support
  const handleMouseDown = useCallback((e) => {
    try {
      if (!e || !e.target || !e.target.closest) return;
      
      if (e.target.closest('.node-element')) return;
      
      const clientX = e.clientX || e.touches?.[0]?.clientX || 0;
      const clientY = e.clientY || e.touches?.[0]?.clientY || 0;
      
      setGraphState(prev => ({
        ...prev,
        isDragging: true,
        dragStart: { x: clientX, y: clientY },
        isTouching: e.touches ? true : false,
        touchStart: { x: clientX, y: clientY }
      }));
    } catch (error) {
      console.error('MathsSolver: Error in handleMouseDown', error);
    }
  }, []);

  const handleMouseMove = useCallback((e) => {
    try {
      const isDragging = getGraphStateValue('isDragging', false);
      const dragStart = getGraphStateValue('dragStart', { x: 0, y: 0 });
      const isTouching = getGraphStateValue('isTouching', false);
      
      if (isDragging) {
        const clientX = e.clientX || e.touches?.[0]?.clientX || 0;
        const clientY = e.clientY || e.touches?.[0]?.clientY || 0;
        
        const deltaX = clientX - dragStart.x;
        const deltaY = clientY - dragStart.y;
        
        // Adjust sensitivity for touch devices
        const sensitivity = isTouching ? 0.3 : 0.5;
        
        setGraphState(prev => ({
          ...prev,
          translation: {
            x: prev.translation.x + deltaX * sensitivity,
            y: prev.translation.y + deltaY * sensitivity
          },
          dragStart: { x: clientX, y: clientY }
        }));
      }
    } catch (error) {
      console.error('MathsSolver: Error in handleMouseMove', error);
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    try {
      setGraphState(prev => ({ 
        ...prev, 
        isDragging: false,
        isTouching: false
      }));
    } catch (error) {
      console.error('MathsSolver: Error in handleMouseUp', error);
    }
  }, []);

  const handleWheel = useCallback((e) => {
    try {
      if (!e || typeof e.preventDefault !== 'function') return;
      
      e.preventDefault();
      const zoomDelta = (e.deltaY || 0) > 0 ? -0.1 : 0.1;
      setGraphState(prev => ({ 
        ...prev, 
        zoom: Math.max(0.3, Math.min(3, prev.zoom + zoomDelta))
      }));
    } catch (error) {
      console.error('MathsSolver: Error in handleWheel', error);
    }
  }, []);

  // Enhanced touch handlers for tablet support
  const handleTouchStart = useCallback((e) => {
    try {
      if (!e || !e.target || !e.target.closest) return;
      
      if (e.target.closest('.node-element')) return;
      
      if (e.touches.length === 1) {
        // Single touch - pan
        const touch = e.touches[0];
        setGraphState(prev => ({
          ...prev,
          isDragging: true,
          isTouching: true,
          dragStart: { x: touch.clientX, y: touch.clientY },
          touchStart: { x: touch.clientX, y: touch.clientY }
        }));
      } else if (e.touches.length === 2) {
        // Two touches - zoom
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const distance = Math.sqrt(
          Math.pow(touch2.clientX - touch1.clientX, 2) +
          Math.pow(touch2.clientY - touch1.clientY, 2)
        );
        
        setGraphState(prev => ({
          ...prev,
          lastTouchDistance: distance
        }));
      }
    } catch (error) {
      console.error('MathsSolver: Error in handleTouchStart', error);
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    try {
      const isDragging = getGraphStateValue('isDragging', false);
      const dragStart = getGraphStateValue('dragStart', { x: 0, y: 0 });
      const lastTouchDistance = getGraphStateValue('lastTouchDistance', 0);
      
      if (e.touches.length === 1 && isDragging) {
        // Single touch - pan
        const touch = e.touches[0];
        const deltaX = touch.clientX - dragStart.x;
        const deltaY = touch.clientY - dragStart.y;
        
        setGraphState(prev => ({
          ...prev,
          translation: {
            x: prev.translation.x + deltaX * 0.3, // Touch sensitivity
            y: prev.translation.y + deltaY * 0.3
          },
          dragStart: { x: touch.clientX, y: touch.clientY }
        }));
      } else if (e.touches.length === 2) {
        // Two touches - zoom
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const distance = Math.sqrt(
          Math.pow(touch2.clientX - touch1.clientX, 2) +
          Math.pow(touch2.clientY - touch1.clientY, 2)
        );
        
        if (lastTouchDistance > 0) {
          const zoomDelta = (distance - lastTouchDistance) * 0.01;
          setGraphState(prev => ({
            ...prev,
            zoom: Math.max(0.3, Math.min(3, prev.zoom + zoomDelta)),
            lastTouchDistance: distance
          }));
        }
      }
    } catch (error) {
      console.error('MathsSolver: Error in handleTouchMove', error);
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    try {
      setGraphState(prev => ({ 
        ...prev, 
        isDragging: false,
        isTouching: false,
        lastTouchDistance: 0
      }));
    } catch (error) {
      console.error('MathsSolver: Error in handleTouchEnd', error);
    }
  }, []);

  // Control functions
  const resetView = () => {
    try {
      setGraphState(prev => ({
        ...prev,
        zoom: 1,
        rotation: { x: 0, y: 0, z: 0 },
        translation: { x: 0, y: 0 },
        selectedNode: null,
        hoveredNode: null,
        highlightedPath: [],
        isAutoRotating: false
      }));
      setFloatingWindow(prev => ({ ...prev, isVisible: false, selectedEntity: null }));
    } catch (error) {
      console.error('MathsSolver: Error in resetView', error);
    }
  };

  const rotateGraph = (axis, degrees) => {
    try {
      if (!axis || typeof axis !== 'string' || typeof degrees !== 'number') {
        console.log('MathsSolver: Invalid parameters for rotateGraph', { axis, degrees });
        return;
      }
      
      setGraphState(prev => ({
        ...prev,
        rotation: {
          ...prev.rotation,
          [axis]: (prev.rotation[axis] + degrees) % 360
        }
      }));
    } catch (error) {
      console.error('MathsSolver: Error in rotateGraph', error);
    }
  };

  const copyToClipboard = (text) => {
    try {
      if (!text || typeof text !== 'string') {
        console.log('MathsSolver: Invalid text for clipboard', { text });
        return;
      }
      
      if (!navigator || !navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
        console.log('MathsSolver: Clipboard API not available');
        return;
      }
      
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('MathsSolver: Error copying to clipboard', error);
    }
  };

  // Professional graph rendering
  // Bulletproof coordinate validation function
  const validateCoordinate = (value, defaultValue = 0) => {
    if (typeof value !== 'number' || isNaN(value) || !isFinite(value)) {
      return defaultValue;
    }
    return value;
  };

  const renderProfessionalGraph = () => {
    try {
      // Early return if no solution or invalid data
      if (!solution || !solution.graphData || !solution.graphData.nodes || !solution.graphData.nodes.length) {
        console.log('MathsSolver: Early return - no solution or invalid data', { solution });
        return null;
      }

      // Additional safety check for graphState
      if (!graphState || typeof graphState.translation !== 'object' || typeof graphState.zoom !== 'number') {
        console.log('MathsSolver: Invalid graphState', { graphState });
        return null;
      }

      // Additional safety check for solution structure
      if (!Array.isArray(solution.graphData.nodes) || !Array.isArray(solution.graphData.links || [])) {
        console.log('MathsSolver: Invalid data structure', { 
          nodes: solution.graphData.nodes, 
          links: solution.graphData.links,
          nodesIsArray: Array.isArray(solution.graphData.nodes),
          linksIsArray: Array.isArray(solution.graphData.links)
        });
        return null;
      }

      // Additional safety check for links array
      if (!solution.graphData.links) {
        console.log('MathsSolver: No links array in graphData', { solution });
        solution.graphData.links = [];
      }

      // Validate all nodes have required properties
      const invalidNodes = solution.graphData.nodes.filter(node => 
        !node.id || typeof node.x !== 'number' || typeof node.y !== 'number' ||
        isNaN(node.x) || isNaN(node.y) || !isFinite(node.x) || !isFinite(node.y)
      );
      
      if (invalidNodes.length > 0) {
        console.error('MathsSolver: Found nodes with invalid coordinates', invalidNodes);
        // Fix invalid nodes by giving them default coordinates
        solution.graphData.nodes = solution.graphData.nodes.map(node => {
          if (typeof node.x !== 'number' || typeof node.y !== 'number' ||
              isNaN(node.x) || isNaN(node.y) || !isFinite(node.x) || !isFinite(node.y)) {
            return {
              ...node,
              x: 0,
              y: 0,
              z: 0
            };
          }
          return node;
        });
      }

      // Debug logging
      console.log('MathsSolver: Starting renderProfessionalGraph', {
        nodesCount: solution.graphData.nodes.length,
        linksCount: solution.graphData.links?.length || 0,
        firstNode: solution.graphData.nodes[0],
        firstLink: solution.graphData.links?.[0]
      });

    const width = 800;
    const height = 600;
    const centerX = width / 2;
    const centerY = height / 2;

    const filteredNodes = solution.graphData.nodes.filter(node => {
      // Safety check for node object
      if (!node || typeof node !== 'object' || !node.id) {
        console.log('MathsSolver: Skipping invalid node in filter', { node });
        return false;
      }
      
      return getFilterSetting(node.type).visible !== false;
    });

    const filteredLinks = getFilterSetting('relationships').visible !== false ? 
      (solution.graphData.links || []).filter(link => {
        // Safety check for link object
        if (!link || typeof link !== 'object') {
          console.log('MathsSolver: Skipping invalid link in filter', { link });
          return false;
        }
        
        const sourceId = link.source || link.from;
        const targetId = link.target || link.to;
        
        // Safety check for source and target IDs
        if (!sourceId || !targetId) {
          console.log('MathsSolver: Skipping link with missing IDs in filter', { link, sourceId, targetId });
          return false;
        }
        
        return filteredNodes.some(n => n.id === sourceId) && 
               filteredNodes.some(n => n.id === targetId);
      }) : [];

    const transformedNodes = filteredNodes.map(node => {
      // Enhanced safety check for node coordinates
      if (typeof node.x !== 'number' || typeof node.y !== 'number' || 
          isNaN(node.x) || isNaN(node.y) || !isFinite(node.x) || !isFinite(node.y)) {
        console.log('MathsSolver: Invalid node coordinates', { 
          node: { id: node.id, name: node.name, x: node.x, y: node.y, z: node.z },
          types: { x: typeof node.x, y: typeof node.y, z: typeof node.z }
        });
        return {
          ...node,
          transformedX: 0,
          transformedY: 0,
          transformedScale: 1,
          depth: 0
        };
      }
      
      try {
        const transformed = apply3DTransform(node.x, node.y, node.z || 0);
        
        // Additional safety check for transformed values
        return {
          ...node,
          transformedX: Number.isFinite(transformed.x) ? transformed.x : 0,
          transformedY: Number.isFinite(transformed.y) ? transformed.y : 0,
          transformedScale: Number.isFinite(transformed.scale) ? transformed.scale : 1,
          depth: Number.isFinite(transformed.depth) ? transformed.depth : 0
        };
      } catch (error) {
        console.error('MathsSolver: Error transforming node', error, { node });
        return {
          ...node,
          transformedX: 0,
          transformedY: 0,
          transformedScale: 1,
          depth: 0
        };
      }
    });

    const sortedNodes = transformedNodes.sort((a, b) => a.depth - b.depth);

    const getNodeColor = (type) => {
      switch (type) {
        case 'concepts': return '#3B82F6'; // Blue
        case 'formulas': return '#10B981'; // Emerald
        case 'values': return '#F59E0B'; // Amber
        case 'steps': return '#8B5CF6'; // Violet
        default: return '#6B7280'; // Gray
      }
    };

    const getNodeIcon = (type) => {
      switch (type) {
        case 'concepts': return '🧠';
        case 'formulas': return '📐';
        case 'values': return '🔢';
        case 'steps': return '✅';
        default: return '⭐';
      }
    };

    const translation = getGraphStateValue('translation', { x: 0, y: 0 });
    const zoom = getGraphStateValue('zoom', 1);
    
    return (
      <g transform={`translate(${centerX + translation.x}, ${centerY + translation.y}) scale(${zoom})`}>
        {/* Links */}
        {filteredLinks.map((link, index) => {
          // Enhanced safety check for link object
          if (!link || typeof link !== 'object') {
            console.log('MathsSolver: Skipping invalid link object', { link });
            return null;
          }
          
          // Handle both 'source/target' and 'from/to' property names
          const sourceId = link.source || link.from;
          const targetId = link.target || link.to;
          
          // Safety check for source and target IDs
          if (!sourceId || !targetId) {
            console.log('MathsSolver: Skipping link due to missing source or target ID', { link, sourceId, targetId });
            return null;
          }
          
          const sourceNode = sortedNodes.find(n => n.id === sourceId);
          const targetNode = sortedNodes.find(n => n.id === targetId);
          
          // Debug logging for link processing
          if (index === 0) {
            console.log('MathsSolver: Processing first link', {
              link,
              sourceId,
              targetId,
              sourceNode: sourceNode ? { id: sourceNode.id, transformedX: sourceNode.transformedX, transformedY: sourceNode.transformedY } : null,
              targetNode: targetNode ? { id: targetNode.id, transformedX: targetNode.transformedX, transformedY: targetNode.transformedY } : null
            });
          }
          
          // Enhanced safety checks for nodes and coordinates
          if (!sourceNode || !targetNode) {
            console.log('MathsSolver: Skipping link due to missing nodes', { link, sourceNode, targetNode });
            return null;
          }
          
          // Check if transformed coordinates exist and are valid numbers
          if (typeof sourceNode.transformedX !== 'number' || 
              typeof sourceNode.transformedY !== 'number' ||
              typeof targetNode.transformedX !== 'number' || 
              typeof targetNode.transformedY !== 'number' ||
              isNaN(sourceNode.transformedX) || isNaN(sourceNode.transformedY) ||
              isNaN(targetNode.transformedX) || isNaN(targetNode.transformedY) ||
              !isFinite(sourceNode.transformedX) || !isFinite(sourceNode.transformedY) ||
              !isFinite(targetNode.transformedX) || !isFinite(targetNode.transformedY)) {
            console.log('MathsSolver: Skipping link due to invalid coordinates', { 
              link, 
              sourceNode: sourceNode ? { 
                id: sourceNode.id, 
                transformedX: sourceNode.transformedX, 
                transformedY: sourceNode.transformedY,
                type: typeof sourceNode.transformedX
              } : null, 
              targetNode: targetNode ? { 
                id: targetNode.id, 
                transformedX: targetNode.transformedX, 
                transformedY: targetNode.transformedY,
                type: typeof targetNode.transformedX
              } : null 
            });
            return null;
          }

          const highlightedPath = getGraphStateValue('highlightedPath', []);
          const isHighlighted = highlightedPath.includes(sourceId) && 
                               highlightedPath.includes(targetId);
          
          const dx = targetNode.transformedX - sourceNode.transformedX;
          const dy = targetNode.transformedY - sourceNode.transformedY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          // Safety check for zero distance to prevent division by zero
          if (distance === 0) {
            return null;
          }
          
          const normalizedDx = dx / distance;
          const normalizedDy = dy / distance;

          const sourceRadius = (sourceNode.radius || 20) * (sourceNode.transformedScale || 1) * getGraphSetting('nodeSize', 1.2);
          const targetRadius = (targetNode.radius || 20) * (targetNode.transformedScale || 1) * getGraphSetting('nodeSize', 1.2);

          const startX = sourceNode.transformedX + normalizedDx * sourceRadius;
          const startY = sourceNode.transformedY + normalizedDy * sourceRadius;
          const endX = targetNode.transformedX - normalizedDx * targetRadius;
          const endY = targetNode.transformedY - normalizedDy * targetRadius;

          // Additional safety check for calculated coordinates
          if (isNaN(startX) || isNaN(startY) || isNaN(endX) || isNaN(endY) ||
              !isFinite(startX) || !isFinite(startY) || !isFinite(endX) || !isFinite(endY)) {
            console.log('MathsSolver: Skipping link due to invalid calculated coordinates', {
              startX, startY, endX, endY,
              sourceNode: { transformedX: sourceNode.transformedX, transformedY: sourceNode.transformedY, radius: sourceRadius },
              targetNode: { transformedX: targetNode.transformedX, transformedY: targetNode.transformedY, radius: targetRadius }
            });
            return null;
          }

          const avgDepth = ((sourceNode.depth || 0) + (targetNode.depth || 0)) / 2;
          const opacity = Math.max(0.3, 1 - Math.abs(avgDepth) / 200);

          // Final safety check before rendering - ensure all values are finite numbers
          const finalStartX = Number.isFinite(startX) ? startX : 0;
          const finalStartY = Number.isFinite(startY) ? startY : 0;
          const finalEndX = Number.isFinite(endX) ? endX : 0;
          const finalEndY = Number.isFinite(endY) ? endY : 0;

          // Additional safety check to prevent SVG errors
          if (!Number.isFinite(finalStartX) || !Number.isFinite(finalStartY) || 
              !Number.isFinite(finalEndX) || !Number.isFinite(finalEndY)) {
            console.error('MathsSolver: Invalid final coordinates for SVG line', {
              finalStartX, finalStartY, finalEndX, finalEndY,
              original: { startX, startY, endX, endY }
            });
            return null;
          }

          // Debug logging for final coordinates
          if (index === 0) {
            console.log('MathsSolver: Final link coordinates', {
              startX, startY, endX, endY,
              finalStartX, finalStartY, finalEndX, finalEndY,
              isFinite: {
                startX: Number.isFinite(startX),
                startY: Number.isFinite(startY),
                endX: Number.isFinite(endX),
                endY: Number.isFinite(endY)
              }
            });
          }

          return (
            <g key={`link-${index}`}>
              <line
                x1={finalStartX} y1={finalStartY} x2={finalEndX} y2={finalEndY}
                stroke={isHighlighted ? "#3B82F6" : "#94A3B8"}
                strokeWidth={(link.width || 2) * getGraphSetting('edgeThickness', 1.5) * Math.min(sourceNode.transformedScale || 1, targetNode.transformedScale || 1)}
                strokeOpacity={opacity * (isHighlighted ? 0.9 : 0.6)}
                markerEnd="url(#arrowhead)"
                className="transition-all duration-800"
                style={{ pointerEvents: 'none' }}
              />
              {getGraphSetting('showRelationshipLabels', true) && distance > 80 && (
                <text
                  x={(finalStartX + finalEndX) / 2}
                  y={(finalStartY + finalEndY) / 2}
                  textAnchor="middle"
                  fontSize={10 * Math.min(sourceNode.transformedScale || 1, targetNode.transformedScale || 1)}
                  fill={isHighlighted ? "#1E40AF" : "#64748B"}
                  fillOpacity={opacity}
                  className="pointer-events-none select-none font-medium"
                >
                  {link.type}
                </text>
              )}
            </g>
          );
        })}

        {/* Nodes */}
        {sortedNodes.map((node, index) => {
          // Enhanced safety check for transformed coordinates
          if (typeof node.transformedX !== 'number' || 
              typeof node.transformedY !== 'number' || 
              typeof node.transformedScale !== 'number' ||
              isNaN(node.transformedX) || isNaN(node.transformedY) || isNaN(node.transformedScale) ||
              !isFinite(node.transformedX) || !isFinite(node.transformedY) || !isFinite(node.transformedScale)) {
            console.log('MathsSolver: Skipping node due to invalid coordinates', { 
              node: { 
                id: node.id, 
                name: node.name,
                transformedX: node.transformedX, 
                transformedY: node.transformedY, 
                transformedScale: node.transformedScale,
                type: typeof node.transformedX
              } 
            });
            return null;
          }
          
          const nodeRadius = (node.radius || 20) * (node.transformedScale || 1) * getGraphSetting('nodeSize', 1.2);
          const opacity = Math.max(0.4, 1 - Math.abs(node.depth || 0) / 200);
          const selectedNode = getGraphStateValue('selectedNode', null);
          const hoveredNode = getGraphStateValue('hoveredNode', null);
          const highlightedPath = getGraphStateValue('highlightedPath', []);
          
          const isSelected = selectedNode === node.id;
          const isHovered = hoveredNode === node.id;
          const isHighlighted = highlightedPath.includes(node.id);
          
          // Final safety check for node coordinates - ensure they are finite numbers
          const finalNodeX = Number.isFinite(node.transformedX) ? node.transformedX : 0;
          const finalNodeY = Number.isFinite(node.transformedY) ? node.transformedY : 0;
          
          // Additional safety check to prevent SVG errors
          if (!Number.isFinite(finalNodeX) || !Number.isFinite(finalNodeY)) {
            console.error('MathsSolver: Invalid final coordinates for SVG circle', {
              finalNodeX, finalNodeY,
              original: { transformedX: node.transformedX, transformedY: node.transformedY }
            });
            return null;
          }
          
          return (
            <g 
              key={node.id} 
              className="node-element cursor-pointer transition-all duration-300"
              style={{
                opacity: opacity,
                transform: `scale(${isSelected ? 1.3 : isHighlighted ? 1.15 : 1})`
              }}
            >
              <circle
                cx={finalNodeX}
                cy={finalNodeY}
                r={nodeRadius}
                fill={getNodeColor(node.type)}
                fillOpacity={opacity * (isHighlighted ? 1 : 0.8)}
                stroke={isSelected ? '#1D4ED8' : isHovered ? '#6366F1' : '#FFFFFF'}
                strokeWidth={isSelected ? 4 * (node.transformedScale || 1) : 3 * (node.transformedScale || 1)}
                className="transition-all duration-200"
                onClick={(e) => handleEntityClick(node, e)}
                onMouseEnter={() => handleEntityHover(node)}
                onMouseLeave={() => handleEntityHover(null)}
                style={{
                  filter: (node.depth || 0) > 0 ? 'brightness(0.85)' : 'brightness(1.1)',
                  cursor: 'pointer'
                }}
              />
              
              {/* Node Icon */}
              <text
                x={finalNodeX}
                y={finalNodeY + 4}
                textAnchor="middle"
                fontSize={12 * (node.transformedScale || 1) * graphSettings.nodeSize}
                className="pointer-events-none select-none"
              >
                {getNodeIcon(node.type)}
              </text>

              {getGraphSetting('showLabels', true) && nodeRadius > 8 && (
                <text
                  x={finalNodeX}
                  y={finalNodeY + nodeRadius + 18 * (node.transformedScale || 1)}
                  textAnchor="middle"
                  fontSize={11 * (node.transformedScale || 1)}
                  fill="#1E293B"
                  fillOpacity={opacity}
                  className="pointer-events-none select-none font-semibold"
                >
                  {node.name}
                </text>
              )}
            </g>
          );
        })}

        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon
              points="0 0, 10 3.5, 0 7"
              fill="#3B82F6"
            />
          </marker>
        </defs>
      </g>
    );
    } catch (error) {
      console.error('MathsSolver: Error in renderProfessionalGraph', error);
      // Return a simple fallback SVG to prevent crashes
      return (
        <g>
          <text x="400" y="300" textAnchor="middle" fill="#666" fontSize="16">
            Error rendering graph. Please try again.
          </text>
        </g>
      );
    }
  };

  // Component definitions
  const MathExpression = ({ children, className = "" }) => {
    if (typeof children !== 'string') {
      return (
        <span className={`font-mono text-sm ${className}`} style={{ fontFamily: 'Computer Modern, serif' }}>
          {children}
        </span>
      );
    }
    
    // Clean the LaTeX expression by removing dollar signs if present
    const cleanLatex = (text) => {
      if (typeof text !== 'string') return text;
      
      // Remove LaTeX delimiters if present
      let cleaned = text
        .replace(/^\$|\$$/g, '') // Remove leading/trailing dollar signs
        .replace(/\\\(|\\\)/g, '') // Remove \( \) delimiters
        .replace(/\\\[|\\\]/g, ''); // Remove \[ \] delimiters
      
      return cleaned;
    };
    
    const cleanedContent = cleanLatex(children);
    
    // Check if the content contains LaTeX expressions (after cleaning)
    const hasLatex = cleanedContent.includes('\\') || 
                     cleanedContent.includes('^') || 
                     cleanedContent.includes('_') || 
                     cleanedContent.includes('frac') ||
                     cleanedContent.includes('sqrt') ||
                     cleanedContent.includes('triangle') ||
                     cleanedContent.includes('angle') ||
                     cleanedContent.includes('perp') ||
                     cleanedContent.includes('parallel') ||
                     cleanedContent.includes('cong') ||
                     cleanedContent.includes('sim') ||
                     cleanedContent.includes('propto') ||
                     cleanedContent.includes('subset') ||
                     cleanedContent.includes('supset') ||
                     cleanedContent.includes('in') ||
                     cleanedContent.includes('cup') ||
                     cleanedContent.includes('cap') ||
                     cleanedContent.includes('emptyset') ||
                     cleanedContent.includes('forall') ||
                     cleanedContent.includes('exists') ||
                     cleanedContent.includes('therefore') ||
                     cleanedContent.includes('because') ||
                     cleanedContent.includes('implies') ||
                     cleanedContent.includes('iff') ||
                     cleanedContent.includes('rightarrow') ||
                     cleanedContent.includes('leftarrow') ||
                     cleanedContent.includes('Rightarrow') ||
                     cleanedContent.includes('Leftarrow') ||
                     cleanedContent.includes('Leftrightarrow');
    
    if (hasLatex) {
      // Use the LaTeX renderer for mathematical expressions
      const renderedHTML = renderMathToHTML(children);
      return (
        <span 
          className={`math-rendered ${className}`}
          dangerouslySetInnerHTML={{ __html: renderedHTML }}
        />
      );
    }
    
    // Handle Unicode superscripts and subscripts for better display
    let processedText = children;
    
    // Convert Unicode superscripts to HTML sup tags
    processedText = processedText.replace(/²/g, '<sup>2</sup>');
    processedText = processedText.replace(/³/g, '<sup>3</sup>');
    processedText = processedText.replace(/¹/g, '<sup>1</sup>');
    processedText = processedText.replace(/⁴/g, '<sup>4</sup>');
    processedText = processedText.replace(/⁵/g, '<sup>5</sup>');
    processedText = processedText.replace(/⁶/g, '<sup>6</sup>');
    processedText = processedText.replace(/⁷/g, '<sup>7</sup>');
    processedText = processedText.replace(/⁸/g, '<sup>8</sup>');
    processedText = processedText.replace(/⁹/g, '<sup>9</sup>');
    processedText = processedText.replace(/⁰/g, '<sup>0</sup>');
    
    // Convert Unicode subscripts to HTML sub tags
    processedText = processedText.replace(/₁/g, '<sub>1</sub>');
    processedText = processedText.replace(/₂/g, '<sub>2</sub>');
    processedText = processedText.replace(/₃/g, '<sub>3</sub>');
    processedText = processedText.replace(/₄/g, '<sub>4</sub>');
    processedText = processedText.replace(/₅/g, '<sub>5</sub>');
    processedText = processedText.replace(/₆/g, '<sub>6</sub>');
    processedText = processedText.replace(/₇/g, '<sub>7</sub>');
    processedText = processedText.replace(/₈/g, '<sub>8</sub>');
    processedText = processedText.replace(/₉/g, '<sub>9</sub>');
    processedText = processedText.replace(/₀/g, '<sub>0</sub>');
    
    // Check if we processed any Unicode characters
    if (processedText !== children) {
      return (
        <span 
          className={`math-rendered ${className}`}
          dangerouslySetInnerHTML={{ __html: processedText }}
        />
      );
    }
    
    // Fallback to regular text rendering
    return (
      <span className={`font-mono text-sm ${className}`} style={{ fontFamily: 'Computer Modern, serif' }}>
        {children}
      </span>
    );
  };

  // COMPLETELY FIXED: Ultra-stable floating window with no flickering
  const FloatingEntityWindow = () => {
    const isVisible = getFloatingWindowValue('isVisible', false);
    const selectedEntity = getFloatingWindowValue('selectedEntity', null);
    
    if (!isVisible || !selectedEntity) return null;

    // Ensure the window doesn't overlap with the statistics section
    const windowY = getFloatingWindowValue('y', 100);
    const windowHeight = getFloatingWindowValue('height', 760);
    const viewportHeight = window.innerHeight;
    const maxY = viewportHeight - windowHeight - 20; // 20px margin from bottom
    
    // Calculate the position of the statistics section to avoid overlap
    const statisticsSection = document.querySelector('[data-statistics-section]');
    let minY = 100; // Default minimum Y position
    
    if (statisticsSection && currentPage === 'solver') {
      const rect = statisticsSection.getBoundingClientRect();
      const statisticsTop = rect.top + window.scrollY;
      const statisticsHeight = rect.height;
      minY = Math.max(100, statisticsTop - windowHeight - 50); // 50px buffer above statistics
    } else {
      // Fallback: if statistics section not found or not on solver page, use a conservative minimum Y
      minY = Math.max(100, windowY - 200); // Keep window at least 200px from top
    }
    
    const adjustedY = Math.min(Math.max(windowY, minY), maxY);

    return (
      <div
        ref={floatingRef}
        className="fixed bg-white rounded-lg shadow-2xl border-2 border-indigo-300 z-50 overflow-hidden"
        style={{
          left: `${getFloatingWindowValue('x', 400)}px`,
          top: `${adjustedY}px`, // Use adjusted Y position to avoid overlap
          width: `${getFloatingWindowValue('width', 400)}px`,
          height: `${getFloatingWindowValue('height', 760)}px`, // Changed from 380 to 760 (twice the height) as requested
          minWidth: '300px',
          minHeight: '250px',
          maxWidth: '800px',
          maxHeight: '600px',
          pointerEvents: 'auto',
          transform: 'translate3d(0, 0, 0)', // Force GPU acceleration
          willChange: getFloatingWindowValue('isDragging', false) || getFloatingWindowValue('isResizing', false) ? 'transform' : 'auto'
        }}
        onMouseDown={handleFloatingMouseDown}
      >
        {/* FIXED: Header with proper drag area */}
        <div 
          className="window-header flex items-center justify-between p-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white cursor-move select-none"
          style={{ cursor: getFloatingWindowValue('isDragging', false) ? 'grabbing' : 'grab' }}
        >
          <div className="flex items-center gap-2 pointer-events-none">
            <Brain className="w-4 h-4" />
            <h4 className="font-semibold text-sm">Mathematical Entity Analysis</h4>
            <span className="text-xs opacity-75">- {selectedEntity.name}</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setFloatingWindow(prev => ({ ...prev, isVisible: false, selectedEntity: null }));
                setGraphState(prev => ({ ...prev, selectedNode: null, highlightedPath: [] }));
              }}
              className="p-1 hover:bg-indigo-700 rounded transition-colors pointer-events-auto"
              style={{ cursor: 'pointer' }}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div 
          className="p-4 overflow-auto" 
          style={{ 
            height: 'calc(100% - 48px)',
            pointerEvents: 'auto'
          }}
        >
                      <div className="space-y-4">
              <div className="p-3 rounded-lg border border-indigo-200 bg-indigo-50">
                <h5 className="font-bold text-lg text-indigo-900">{selectedEntity.name || 'Unknown Entity'}</h5>
                <p className="text-sm text-indigo-700 mt-1">{selectedEntity.description || 'No description available'}</p>
                <div className="mt-3">
                  <MathExpression className="text-indigo-800 bg-indigo-100 px-3 py-2 rounded block">
                    {selectedEntity.value || 'No value available'}
                  </MathExpression>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <span className="inline-block px-2 py-1 text-xs rounded-full bg-indigo-200 text-indigo-800 font-medium">
                    {selectedEntity.type || 'unknown'}
                  </span>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-indigo-600 mr-1">Importance:</span>
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className={`w-3 h-3 ${i < (selectedEntity.importance || 0) / 2 ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
                      />
                    ))}
                    <span className="text-xs text-indigo-600 ml-1">{selectedEntity.importance || 0}/10</span>
                  </div>
                </div>
              </div>

            <div>
              <h6 className="font-semibold text-indigo-900 mb-3 flex items-center gap-2">
                <Network className="w-4 h-4" />
                Mathematical Relationships
              </h6>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {solution?.graphData?.links
                  .filter(link => {
                    const sourceId = link.source || link.from;
                    const targetId = link.target || link.to;
                    return sourceId === selectedEntity.id || targetId === selectedEntity.id;
                  })
                  .map((relation, index) => {
                    const sourceId = relation.source || relation.from;
                    const targetId = relation.target || relation.to;
                    const relatedId = sourceId === selectedEntity.id ? targetId : sourceId;
                    const relatedEntity = solution?.graphData?.nodes?.find(e => e.id === relatedId);
                    const isOutgoing = sourceId === selectedEntity.id;

                    return (
                      <div 
                        key={index} 
                        className="p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (relatedEntity) {
                            setFloatingWindow(prev => ({ ...prev, selectedEntity: relatedEntity }));
                            setGraphState(prev => ({ 
                              ...prev, 
                              selectedNode: relatedEntity.id,
                              highlightedPath: findConnectedNodes(relatedEntity.id, solution?.graphData)
                            }));
                          }
                        }}
                      >
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-medium text-indigo-900">
                            {isOutgoing ? selectedEntity.name : relatedEntity?.name}
                          </span>
                          <span className="text-indigo-600 italic font-medium">{relation.type}</span>
                          <span className="font-medium text-indigo-900">
                            {isOutgoing ? relatedEntity?.name : selectedEntity.name}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <p className="text-xs text-gray-600">{relatedEntity?.description}</p>
                          <span className="text-xs text-indigo-500 font-medium">
                            Strength: {relation.strength}/10
                          </span>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <h6 className="font-semibold text-amber-900 mb-2">Mathematical Value</h6>
              <MathExpression className="text-amber-800 bg-amber-100 px-3 py-2 rounded block">
                {selectedEntity.value}
              </MathExpression>
            </div>
          </div>
        </div>

        {/* FIXED: Ultra-stable resize handle */}
        <div 
          className="resize-handle absolute bottom-0 right-0 w-4 h-4 cursor-se-resize bg-gradient-to-br from-indigo-300 to-purple-400 hover:from-indigo-400 hover:to-purple-500 transition-all duration-200"
          style={{
            clipPath: 'polygon(100% 0%, 0% 100%, 100% 100%)',
            cursor: getFloatingWindowValue('isResizing', false) ? 'se-resize' : 'se-resize'
          }}
        >
          <div className="absolute bottom-1 right-1 w-2 h-2 border-r border-b border-white opacity-60"></div>
        </div>
      </div>
    );
  };

  // Filter all entities for search and list view
  const allEntities = solution?.graphData?.nodes || [];
  const filteredEntities = allEntities.filter(entity => {
    // Safety check for entity object
    if (!entity || typeof entity !== 'object') {
      return false;
    }
    
    const searchLower = (searchTerm || '').toLowerCase();
    return (entity.name && entity.name.toLowerCase().includes(searchLower)) ||
           (entity.description && entity.description.toLowerCase().includes(searchLower)) ||
           (entity.value && entity.value.toLowerCase().includes(searchLower));
  });

  // Determine if current problem is HKDSE or Topics
  const isHKDSEProblem = () => {
    if (selectedSample) {
      // Check if selectedSample has HKDSE-specific properties
      return (
        (selectedSample.id && selectedSample.id.includes('hkdse')) ||
        (selectedSample.problem && selectedSample.problem.includes('HKDSE')) ||
        (selectedSample.analysis && selectedSample.analysis.includes('HKDSE')) ||
        (selectedSample.problemType && selectedSample.problemType.includes('HKDSE')) ||
        (selectedSample.graphData && selectedSample.graphData.nodes && 
         selectedSample.graphData.nodes.some(node => 
           node.name && node.name.includes('HKDSE')
         ))
      );
    }
    return false;
  };

  const getCurrentProblemType = () => {
    if (isHKDSEProblem()) {
      return 'HKDSE';
    }
    return 'Topics';
  };

  // Generate HKDSE JSON structure
  const getHKDSEJsonStructure = () => {
    return {
      problem: "HKDSE 2021 Sample Problem",
      problemType: "quadratic",
      analysis: "Detailed HKDSE-style analysis with comprehensive solution steps",
      graphData: {
        nodes: [
          {
            id: "problem_statement",
            name: "Problem Statement",
            type: "concepts",
            description: "The mathematical problem to be solved",
            value: "Solve x^2 + 5x + 6 = 0",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "solution_methods",
            name: "Solution Methods",
            type: "concepts",
            description: "Available methods to solve this problem",
            value: "Factoring, Quadratic Formula, Completing the Square",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "step1_analysis",
            name: "Step 1: Problem Analysis",
            type: "steps",
            description: "Analyze the problem structure and identify key elements",
            value: "Identify coefficients: a=1, b=5, c=6",
            importance: 8,
            x: 0, y: 0, z: 25
          },
          {
            id: "step2_method_selection",
            name: "Step 2: Method Selection",
            type: "steps",
            description: "Choose the most appropriate solution method",
            value: "Use factoring since a=1 and c is positive",
            importance: 9,
            x: 0, y: 50, z: 0
          },
          {
            id: "step3_calculation",
            name: "Step 3: Perform Calculations",
            type: "steps",
            description: "Execute the mathematical calculations",
            value: "Find factors of 6 that add to 5: 2 and 3",
            importance: 9,
            x: 0, y: 100, z: 0
          },
          {
            id: "step4_verification",
            name: "Step 4: Verify Solution",
            type: "steps",
            description: "Check the solution for correctness",
            value: "Verify: (x + 2)(x + 3) = x^2 + 5x + 6",
            importance: 8,
            x: -100, y: 200, z: 0
          },
          {
            id: "intermediate_values",
            name: "Intermediate Values",
            type: "values",
            description: "Key numerical results from calculations",
            value: "Factors: 2 and 3",
            importance: 7,
            x: 100, y: 200, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "The complete solution to the problem",
            value: "x = -2 or x = -3",
            importance: 10,
            x: 0, y: 250, z: 0
          },
          {
            id: "hkdse_specific_concepts",
            name: "HKDSE-Specific Concepts",
            type: "concepts",
            description: "Concepts specific to HKDSE examination format",
            value: "Examination techniques, time management, presentation",
            importance: 8,
            x: 200, y: 150, z: 0
          }
        ],
        links: [
          { from: "problem_statement", to: "solution_methods", type: "enables", strength: 8 },
          { from: "solution_methods", to: "step1_analysis", type: "leads to", strength: 9 },
          { from: "step1_analysis", to: "step2_method_selection", type: "leads to", strength: 9 },
          { from: "step2_method_selection", to: "step3_calculation", type: "leads to", strength: 9 },
          { from: "step3_calculation", to: "intermediate_values", type: "calculates", strength: 8 },
          { from: "step3_calculation", to: "step4_verification", type: "leads to", strength: 8 },
          { from: "step4_verification", to: "final_answer", type: "leads to", strength: 10 },
          { from: "intermediate_values", to: "final_answer", type: "contributes to", strength: 9 },
          { from: "hkdse_specific_concepts", to: "step1_analysis", type: "guides", strength: 7 }
        ]
      }
    };
  };

  // Generate Topics JSON structure
  const getTopicsJsonStructure = () => {
    return {
      problem: "Sample quadratic equation",
      problemType: "quadratic",
      analysis: "Comprehensive analysis of the quadratic equation",
      graphData: {
        nodes: [
          {
            id: "problem_statement",
            name: "Problem Statement",
            type: "concepts",
            description: "The mathematical problem to be solved",
            value: "x^2 + 5x + 6 = 0",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "key_concepts",
            name: "Key Mathematical Concepts",
            type: "concepts",
            description: "Fundamental concepts and principles involved",
            value: "Quadratic equations, factoring, roots",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "solution_method",
            name: "Solution Method",
            type: "formulas",
            description: "Mathematical method to solve this problem",
            value: "Factoring method: (x + 2)(x + 3) = 0",
            importance: 9,
            x: -120, y: 100, z: -15
          },
          {
            id: "step1_analysis",
            name: "Step 1: Problem Analysis",
            type: "steps",
            description: "Analyze the problem structure and identify key elements",
            value: "Identify coefficients: a=1, b=5, c=6",
            importance: 8,
            x: 120, y: 100, z: -15
          },
          {
            id: "step2_method",
            name: "Step 2: Choose Method",
            type: "steps",
            description: "Select appropriate mathematical method",
            value: "Use factoring since a=1 and c is positive",
            importance: 9,
            x: 0, y: 50, z: 40
          },
          {
            id: "step3_calculation",
            name: "Step 3: Perform Calculations",
            type: "steps",
            description: "Execute the mathematical calculations",
            value: "Find factors of 6 that add to 5: 2 and 3",
            importance: 9,
            x: 0, y: 150, z: -20
          },
          {
            id: "step4_verification",
            name: "Step 4: Verify Solution",
            type: "steps",
            description: "Check the solution for correctness",
            value: "Verify: (x + 2)(x + 3) = x^2 + 5x + 6",
            importance: 8,
            x: -100, y: 200, z: 0
          },
          {
            id: "intermediate_values",
            name: "Intermediate Values",
            type: "values",
            description: "Key numerical results from calculations",
            value: "Factors: 2 and 3",
            importance: 7,
            x: 100, y: 200, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "The complete solution to the problem",
            value: "x = -2 or x = -3",
            importance: 10,
            x: 0, y: 250, z: 0
          }
        ],
        links: [
          { from: "problem_statement", to: "key_concepts", type: "enables", strength: 8 },
          { from: "key_concepts", to: "solution_method", type: "enables", strength: 9 },
          { from: "solution_method", to: "step1_analysis", type: "leads to", strength: 8 },
          { from: "step1_analysis", to: "step2_method", type: "leads to", strength: 9 },
          { from: "step2_method", to: "step3_calculation", type: "leads to", strength: 9 },
          { from: "step3_calculation", to: "intermediate_values", type: "calculates", strength: 8 },
          { from: "step3_calculation", to: "step4_verification", type: "leads to", strength: 8 },
          { from: "step4_verification", to: "final_answer", type: "leads to", strength: 10 },
          { from: "intermediate_values", to: "final_answer", type: "contributes to", strength: 9 }
        ]
      }
    };
  };

  // Get current JSON structure based on problem type
  const getCurrentJsonStructure = () => {
    if (isHKDSEProblem()) {
      return getHKDSEJsonStructure();
    }
    return getTopicsJsonStructure();
  };

  // Get actual HKDSE problem data for dynamic JSON display
  const getActualHKDSEProblemData = () => {
    if (!selectedSample) {
      return getHKDSEJsonStructure(); // Fallback to static structure
    }

    // Try to find the actual problem data
    try {
      // Extract year from selectedSample.id (e.g., "hkdse2021" -> "2021")
      const yearMatch = selectedSample.id?.match(/hkdse(\d{4})/);
      if (!yearMatch) {
        return getHKDSEJsonStructure();
      }

      const year = yearMatch[1];
      const yearKey = `hkdse${year}`;
      
      // Import the actual HKDSE data dynamically
      let actualProblemData = null;
      
      // Try to find the problem in the actual data
      if (selectedSample.problem && selectedSample.graphData) {
        // Use the selectedSample data directly if it has graphData
        actualProblemData = {
          problem: selectedSample.problem,
          problemType: selectedSample.problemType || 'quadratic',
          analysis: `HKDSE ${year} ${selectedSample.problemType || 'mathematics'} problem analysis`,
          graphData: selectedSample.graphData
        };
      } else {
        // Fallback to static structure
        actualProblemData = getHKDSEJsonStructure();
      }

      return actualProblemData;
    } catch (error) {
      console.error('Error getting actual HKDSE problem data:', error);
      return getHKDSEJsonStructure();
    }
  };

  // Get actual Topics problem data for dynamic JSON display
  const getActualTopicsProblemData = () => {
    if (!selectedSample) {
      return getTopicsJsonStructure(); // Fallback to static structure
    }

    try {
      // Use the selectedSample data directly if it has graphData
      if (selectedSample.problem && selectedSample.graphData) {
        return {
          problem: selectedSample.problem,
          problemType: selectedSample.problemType || 'quadratic',
          analysis: `Topics ${selectedSample.problemType || 'mathematics'} problem analysis`,
          graphData: selectedSample.graphData
        };
      } else {
        // Fallback to static structure
        return getTopicsJsonStructure();
      }
    } catch (error) {
      console.error('Error getting actual Topics problem data:', error);
      return getTopicsJsonStructure();
    }
  };

  // Get dynamic JSON structure based on actual problem data
  const getDynamicJsonStructure = () => {
    if (isHKDSEProblem()) {
      return getActualHKDSEProblemData();
    }
    return getActualTopicsProblemData();
  };

  // Get current problem information for display
  const getCurrentProblemInfo = () => {
    if (!selectedSample) {
      return {
        name: 'No problem selected',
        type: getCurrentProblemType(),
        description: 'Select a problem to see its JSON structure'
      };
    }

    return {
      name: selectedSample.id || 'Unknown Problem',
      type: getCurrentProblemType(),
      description: selectedSample.description || 'Mathematical problem analysis',
      problemType: selectedSample.problemType || 'quadratic',
      difficulty: selectedSample.difficulty || 'intermediate'
    };
  };

  // Connection status state
  const [connectionStatus, setConnectionStatus] = useState('idle'); // 'idle', 'testing', 'connected', 'failed'

  // Test connection on component mount
  useEffect(() => {
    const testConnection = async () => {
      setConnectionStatus('testing');
      try {
        const result = await deepSeekThinkService.testConnection();
        setConnectionStatus(result.success ? 'connected' : 'failed');
      } catch (error) {
        setConnectionStatus('failed');
      }
    };
    
    testConnection();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Professional Header */}
      <div className="bg-white shadow-lg border-b border-indigo-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Maths AI Solver
                </h1>
                <p className="text-sm text-indigo-600 font-medium">Professional mathematical analysis with interactive knowledge graphs</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Connection Status Indicator */}
              <div className="flex items-center space-x-2">
                <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium ${
                  connectionStatus === 'connected' ? 'bg-green-100 text-green-800' :
                  connectionStatus === 'testing' ? 'bg-yellow-100 text-yellow-800' :
                  connectionStatus === 'failed' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  <div className={`w-2 h-2 rounded-full ${
                    connectionStatus === 'connected' ? 'bg-green-500' :
                    connectionStatus === 'testing' ? 'bg-yellow-500 animate-pulse' :
                    connectionStatus === 'failed' ? 'bg-red-500' :
                    'bg-gray-500'
                  }`}></div>
                  <span>
                    {connectionStatus === 'connected' ? 'DeepSeek API Connected' :
                     connectionStatus === 'testing' ? 'Testing Connection...' :
                     connectionStatus === 'failed' ? 'Connection Failed' :
                     'Checking Connection...'}
                  </span>
                  {connectionStatus === 'failed' && (
                    <button
                      onClick={async () => {
                        setConnectionStatus('testing');
                        try {
                          const result = await deepSeekThinkService.testConnection();
                          setConnectionStatus(result.success ? 'connected' : 'failed');
                        } catch (error) {
                          setConnectionStatus('failed');
                        }
                      }}
                      className="ml-2 px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                    >
                      Retry
                    </button>
                  )}
                </div>
              </div>
              
              {/* Problem Selector Dropdown */}
              {selectedSample && supportsProblemDropdown(selectedSample) && (
                <div className="relative problem-dropdown">
                  <button
                    onClick={() => setIsProblemDropdownOpen(!isProblemDropdownOpen)}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg border border-indigo-200 transition-colors"
                  >
                    <span className="text-sm font-medium">
                      Problem {selectedProblemNumber || '?'}
                    </span>
                    <ChevronDownIcon className={`w-4 h-4 transition-transform ${isProblemDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {isProblemDropdownOpen && (
                    <div className="absolute top-full right-0 mt-1 w-96 max-h-96 overflow-y-auto bg-white border border-indigo-200 rounded-lg shadow-lg z-50">
                      <div className="p-2">
                        <div className="text-xs font-semibold text-indigo-600 mb-2 px-2">
                          {(() => {
                            const { sampleType, year } = getSampleInfo(selectedSample);
                            return getDropdownTitle(sampleType, year);
                          })()}
                        </div>
                        {(() => {
                          const { sampleType, year } = getSampleInfo(selectedSample);
                          return getFormattedProblemList(sampleType, year).map((problem) => (
                            <button
                              key={problem.value}
                              onClick={() => handleProblemSelection(problem.value)}
                              className={`w-full text-left px-3 py-2 rounded text-sm hover:bg-indigo-50 transition-colors ${
                                selectedProblemNumber === problem.value ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-700'
                              }`}
                            >
                              <div className="flex items-start gap-2">
                                <span className="text-indigo-600 font-medium min-w-fit">
                                  Problem {problem.value}:
                                </span>
                                <div className="flex-1">
                                  <MathExpression className="text-sm leading-relaxed">
                                    {problem.problem.problem}
                                  </MathExpression>
                                </div>
                              </div>
                            </button>
                          ));
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              )}
              

              
              {/* View Mode Toggle */}
              <div className="flex items-center gap-2 bg-indigo-100 rounded-lg p-1">
                <button
                  onClick={handleGraphViewClick}
                  className={`p-3 rounded-md text-sm font-medium transition-colors ${
                    currentPage === 'graph-view' 
                      ? 'bg-white text-indigo-600 shadow-sm' 
                      : 'text-indigo-700 hover:text-indigo-900'
                  }`}
                  title="Graph View"
                >
                  <Network className="w-5 h-5" />
                </button>
                <button
                  onClick={handleAnalysisViewClick}
                  className={`p-3 rounded-md text-sm font-medium transition-colors ${
                    currentPage === 'analysis-view' 
                      ? 'bg-white text-indigo-600 shadow-sm' 
                      : 'text-indigo-700 hover:text-indigo-900'
                  }`}
                  title="Analysis View"
                >
                  <Brain className="w-5 h-5" />
                </button>
                <button
                  onClick={handleToolBoxClick}
                  className={`p-3 rounded-md text-sm font-medium transition-colors ${
                    currentPage === 'toolbox'
                      ? 'bg-white text-indigo-600 shadow-sm'
                      : 'text-indigo-700 hover:text-indigo-900'
                  }`}
                  title="ToolBox"
                >
                  <Wrench className="w-5 h-5" />
                </button>
                <button
                  onClick={handleAIGeneratedClick}
                  className={`p-3 rounded-md text-sm font-medium transition-colors ${
                    currentPage === 'ai-generated'
                      ? 'bg-white text-indigo-600 shadow-sm'
                      : 'text-indigo-700 hover:text-indigo-900'
                  }`}
                  title="AI Generated"
                >
                  <Sparkles className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Enhanced Input Section */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-indigo-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-indigo-900 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-indigo-600" />
              {problemType === 'quadratic' ? 'Quadratic Equation Input' :
               problemType === 'circle_geometry' ? 'Circle Geometry Input' :
               problemType === 'factor_remainder' ? 'Factor & Remainder Input' :
               problemType === 'probability' ? 'Probability Input' :
               problemType === 'statistics' ? 'Statistics Input' :
               problemType === 'trigonometry' ? 'Trigonometry Input' :
               'Mathematical Problem Input'}
              {selectedSample && (
                <span className="ml-2 text-sm text-green-600 font-medium">
                  (Sample Loaded)
                </span>
              )}
            </h2>
            <div className="flex items-center gap-2">
              {selectedSample && onSampleClear && (
                <button
                  onClick={() => {
                    setLatexInput('');
                    setSolution(null);
                    if (onSampleClear) onSampleClear();
                  }}
                  className="flex items-center gap-2 px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm"
                >
                  <X className="w-4 h-4" />
                  Clear Sample
                </button>
              )}
              <button
                onClick={() => {
                  console.log('MathsSolver: Solve with AI button clicked', { latexInput, problemType });
                  // Use sample data approach for "Solve with AI"
                  solveProblem(latexInput);
                }}
                disabled={!latexInput || isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 transition-all"
                title="Quick analysis using sample data patterns"
              >
                {isLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Brain className="w-4 h-4" />
                    Solve with AI
                  </>
                )}
              </button>
              <button
                onClick={handleThinkAboutProblem}
                disabled={!latexInput || isThinking}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-lg hover:from-amber-700 hover:to-orange-700 disabled:opacity-50 transition-all"
                title="Deep AI analysis with comprehensive knowledge graph generation"
              >
                {isThinking ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Thinking...
                  </>
                ) : (
                  <>
                    <Lightbulb className="w-4 h-4" />
                    Let's think
                  </>
                )}
              </button>
              {/* JSON Download and Import buttons */}
              <button
                onClick={handleDownloadJson}
                className="flex items-center gap-2 px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors text-sm"
                title="Download current Knowledge Graph as JSON"
              >
                <FileText className="w-4 h-4" />
                JSON
              </button>
              <button
                onClick={handleImportClick}
                className="flex items-center gap-2 px-3 py-2 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors text-sm"
                title="Import JSON"
              >
                <Upload className="w-4 h-4" />
                Import
              </button>
            </div>
          </div>

          {/* Dynamic JSON Structure Display */}
          <div className="mb-6 bg-white rounded-xl shadow-lg border border-indigo-200 p-6">
            {/* Hidden file input for Import button */}
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={handleFileChange}
            />
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-indigo-900 flex items-center gap-2">
                <Code className="w-5 h-5" />
                Maths AI Solver - JSON Structure Reference
              </h3>
              <button
                onClick={() => setShowJsonStructures(!showJsonStructures)}
                className="flex items-center gap-2 px-3 py-1 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-lg transition-colors text-sm"
              >
                {showJsonStructures ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {showJsonStructures ? 'Hide' : 'Show'} JSON Structures
              </button>
            </div>
            
            {showJsonStructures && (
              <>
                <div className="mb-4 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Info className="w-4 h-4 text-indigo-600" />
                    <span className="font-medium text-indigo-800">
                      Current Problem: {getCurrentProblemInfo().name} ({getCurrentProblemInfo().type})
                    </span>
                  </div>
                  <div className="text-indigo-700 text-sm space-y-1">
                    <p><strong>Description:</strong> {getCurrentProblemInfo().description}</p>
                    <p><strong>Problem Type:</strong> {getCurrentProblemInfo().problemType}</p>
                    <p><strong>Difficulty:</strong> {getCurrentProblemInfo().difficulty}</p>
                    <p><strong>JSON Structure:</strong> This shows the actual JSON structure from the selected problem for designing fallback structures.</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Current Problem Type Structure */}
                  <div className={`rounded-lg p-4 border ${
                    isHKDSEProblem() 
                      ? 'bg-blue-50 border-blue-200' 
                      : 'bg-emerald-50 border-emerald-200'
                  }`}>
                                         <h4 className={`font-semibold mb-3 flex items-center gap-2 ${
                       isHKDSEProblem() ? 'text-blue-900' : 'text-emerald-900'
                     }`}>
                       <Database className="w-4 h-4" />
                       {getCurrentProblemType()} JSON Structure
                       {selectedSample && (
                         <span className="text-xs text-gray-500 ml-2">
                           (Dynamic from {selectedSample.id || 'selected problem'})
                         </span>
                       )}
                     </h4>
                     <div className={`text-green-400 p-3 rounded text-xs overflow-x-auto max-h-64 overflow-y-auto ${
                       isHKDSEProblem() ? 'bg-blue-900' : 'bg-emerald-900'
                     }`}>
                       <pre>{JSON.stringify(getDynamicJsonStructure(), null, 2)}</pre>
                     </div>
                  </div>

                  {/* Alternative Structure */}
                  <div className={`rounded-lg p-4 border ${
                    isHKDSEProblem() 
                      ? 'bg-emerald-50 border-emerald-200' 
                      : 'bg-blue-50 border-blue-200'
                  }`}>
                                         <h4 className={`font-semibold mb-3 flex items-center gap-2 ${
                       isHKDSEProblem() ? 'text-emerald-900' : 'text-blue-900'
                     }`}>
                       <FileText className="w-4 h-4" />
                       {isHKDSEProblem() ? 'Topics' : 'HKDSE'} JSON Structure
                       <span className="text-xs text-gray-500 ml-2">
                         (Reference Structure)
                       </span>
                     </h4>
                     <div className={`text-green-400 p-3 rounded text-xs overflow-x-auto max-h-64 overflow-y-auto ${
                       isHKDSEProblem() ? 'bg-emerald-900' : 'bg-blue-900'
                     }`}>
                       <pre>{JSON.stringify(
                         isHKDSEProblem() ? getTopicsJsonStructure() : getHKDSEJsonStructure(), 
                         null, 
                         2
                       )}</pre>
                     </div>
                  </div>
                </div>

                {/* Usage Instructions */}
                <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb className="w-4 h-4 text-yellow-600" />
                    <span className="font-medium text-yellow-800">How to Use These JSON Structures</span>
                  </div>
                  <div className="text-yellow-700 text-sm space-y-1">
                    <p>• <strong>Dynamic HKDSE Structure:</strong> Shows actual JSON data from the selected HKDSE problem - use this to design fallback structures that match the real data format</p>
                    <p>• <strong>Dynamic Topics Structure:</strong> Shows actual JSON data from the selected Topics problem - use this to understand the real data structure</p>
                    <p>• <strong>Reference Structure:</strong> Shows the standard template structure for comparison and fallback design</p>
                    <p>• <strong>AI Integration:</strong> When using "Let's think", the AI will reference the actual problem structure for better results</p>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Sample Equations */}
          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              {problemType === 'quadratic' ? 'Sample Equations (Click to try)' :
               problemType === 'circle_geometry' ? 'Sample Circle Geometry Problems (Click to try)' :
               problemType === 'factor_remainder' ? 'Sample Factor & Remainder Problems (Click to try)' :
               problemType === 'probability' ? 'Sample Probability Problems (Click to try)' :
               problemType === 'statistics' ? 'Sample Statistics Problems (Click to try)' :
               problemType === 'trigonometry' ? 'Sample Trigonometry Problems (Click to try)' :
               'Sample Problems (Click to try)'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2">
              {sampleEquations.map((eq, index) => (
                <button
                  key={index}
                  onClick={() => {
                    console.log('MathsSolver: Sample equation clicked', { eq });
                    setLatexInput(eq);
                    // Automatically solve when a sample equation is clicked
                    setTimeout(() => solveProblem(eq), 100);
                  }}
                  className="p-2 bg-gray-50 hover:bg-indigo-50 rounded-lg border border-gray-200 hover:border-indigo-300 transition-all duration-200 text-left"
                >
                  <MathExpression className="text-gray-700 text-xs">{eq}</MathExpression>
                </button>
              ))}
            </div>
          </div>
          
          <div className="flex space-x-3">
            <input
              type="text"
              value={latexInput}
              onChange={(e) => setLatexInput(e.target.value)}
              placeholder={
                problemType === 'quadratic' ? "Enter quadratic equation (e.g., x^2 + 5x + 6 = 0)" :
                problemType === 'circle_geometry' ? "Enter circle geometry problem (e.g., Find equation of circle with center (3, -2) and radius 5)" :
                problemType === 'factor_remainder' ? "Enter factor/remainder problem (e.g., Factor x^3 - 8)" :
                problemType === 'probability' ? "Enter probability problem (e.g., Find probability of rolling a 6 on a fair die)" :
                problemType === 'statistics' ? "Enter statistics problem (e.g., Find mean of numbers: 2, 4, 6, 8, 10)" :
                problemType === 'trigonometry' ? "Enter trigonometry problem (e.g., Find sin(30°))" :
                "Enter mathematical problem"
              }
              className="flex-1 p-3 border-2 border-indigo-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <button
              onClick={() => copyToClipboard(latexInput)}
              className="px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              title="Copy equation"
            >
              {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5 text-gray-500" />}
            </button>
          </div>
          
          {latexInput && (
            <div className="mt-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
              <span className="text-sm text-indigo-700 font-medium">Preview: </span>
              <MathExpression className="text-indigo-800">{latexInput}</MathExpression>
            </div>
          )}
        </div>

        {viewMode === 'graph' ? (
          /* Enhanced Graph Visualization Mode */
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
            {/* Advanced Controls Panel */}
            <div className="xl:col-span-1 space-y-4">
              {/* Search */}
              <div className="bg-white rounded-xl shadow-lg p-4 border border-indigo-200">
                <h3 className="font-semibold text-indigo-900 mb-3 flex items-center gap-2">
                  <Search className="w-4 h-4" />
                  Search & Filter
                </h3>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search mathematical entities..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border-2 border-indigo-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Entity Filters */}
              <div className="bg-white rounded-xl shadow-lg p-4 border border-indigo-200">
                <h3 className="font-semibold text-indigo-900 mb-3 flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  Entity Types
                </h3>
                <div className="space-y-3">
                  {Object.entries(filterSettings).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between">
                      <label className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={value.visible}
                          onChange={(e) => setFilterSettings(prev => ({ 
                            ...prev, 
                            [key]: { ...prev[key], visible: e.target.checked }
                          }))}
                          className="rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500 w-4 h-4"
                        />
                        <span className="ml-2 text-indigo-700 font-medium capitalize">{key}</span>
                      </label>
                      <button
                        onClick={() => setFilterSettings(prev => ({ 
                          ...prev, 
                          [key]: { ...prev[key], isolated: !prev[key].isolated }
                        }))}
                        className={`p-1 rounded transition-colors ${
                          value.isolated ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-400'
                        }`}
                        title={`${value.isolated ? 'Show all' : 'Isolate'} ${key}`}
                      >
                        {value.isolated ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Graph Settings */}
              <div className="bg-white rounded-xl shadow-lg p-4 border border-indigo-200">
                <h3 className="font-semibold text-indigo-900 mb-3 flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Display Settings
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Node Size</label>
                    <input
                      type="range"
                      min="0.5"
                      max="2"
                      step="0.1"
                      value={graphSettings.nodeSize}
                      onChange={(e) => setGraphSettings(prev => ({ ...prev, nodeSize: parseFloat(e.target.value) }))}
                      className="w-full"
                    />
                    <span className="text-xs text-gray-500">{graphSettings.nodeSize.toFixed(1)}x</span>
                  </div>
                  
                  <div>
                    <label className="text-sm text-gray-600 block mb-1">Edge Thickness</label>
                    <input
                      type="range"
                      min="0.5"
                      max="3"
                      step="0.1"
                      value={graphSettings.edgeThickness}
                      onChange={(e) => setGraphSettings(prev => ({ ...prev, edgeThickness: parseFloat(e.target.value) }))}
                      className="w-full"
                    />
                    <span className="text-xs text-gray-500">{graphSettings.edgeThickness.toFixed(1)}x</span>
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center text-sm">
                      <input
                        type="checkbox"
                        checked={graphSettings.showLabels}
                        onChange={(e) => setGraphSettings(prev => ({ ...prev, showLabels: e.target.checked }))}
                        className="rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500 w-4 h-4"
                      />
                      <span className="ml-2 text-indigo-700 font-medium">Show Labels</span>
                    </label>
                    
                    <label className="flex items-center text-sm">
                      <input
                        type="checkbox"
                        checked={graphSettings.showRelationshipLabels}
                        onChange={(e) => setGraphSettings(prev => ({ ...prev, showRelationshipLabels: e.target.checked }))}
                        className="rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500 w-4 h-4"
                      />
                      <span className="ml-2 text-indigo-700 font-medium">Relationship Labels</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Professional Graph Visualization */}
            <div className="xl:col-span-3">
              <div className="bg-white rounded-xl shadow-lg p-6 border border-indigo-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-indigo-900 flex items-center gap-2">
                    <Network className="w-5 h-5 text-indigo-600" />
                    Interactive Mathematical Knowledge Graph
                    {graphState.selectedNode && solution && (
                      <span className="ml-2 text-sm font-normal text-blue-600">
                        - {solution?.graphData?.nodes?.find(n => n.id === graphState.selectedNode)?.name}
                      </span>
                    )}
                  </h3>
                  <div className="flex items-center gap-4 text-sm text-indigo-600">
                    <div className="flex items-center gap-1">
                      <Hand className="w-4 h-4" />
                      <span>Drag to pan</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MousePointer className="w-4 h-4" />
                      <span>Click entities</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <RotateCw className="w-4 h-4" />
                      <span>Use controls to rotate</span>
                    </div>
                  </div>
                </div>
                
                <div className="relative border-2 border-indigo-300 rounded-lg overflow-hidden bg-gradient-to-br from-indigo-100 via-purple-100 to-cyan-100">
                  {/* Enhanced Control Panels */}
                  
                  {/* Top-Left: Entity Filters Panel */}
                  <div className="absolute top-4 left-4 z-20 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3 border-2 border-indigo-200">
                    <h4 className="font-semibold text-sm text-indigo-900 mb-2 flex items-center gap-1">
                      <Filter className="w-3 h-3" />
                      Quick Filters
                    </h4>
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      {Object.entries(filterSettings).map(([key, value]) => (
                        <label key={key} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={value.visible}
                            onChange={(e) => setFilterSettings(prev => ({
                              ...prev,
                              [key]: { ...prev[key], visible: e.target.checked }
                            }))}
                            className="rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500 w-3 h-3"
                          />
                          <span className="ml-1 text-indigo-700 font-medium capitalize">{key}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Bottom-Left: View Controls Panel */}
                  <div className="absolute bottom-4 left-4 z-20 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3 border-2 border-indigo-200">
                    <h4 className="font-semibold text-sm text-indigo-900 mb-2 flex items-center gap-1">
                      <Settings className="w-3 h-3" />
                      View Controls
                    </h4>
                    
                    {/* Auto Rotate Toggle */}
                    <button
                      onClick={() => setGraphState(prev => ({ ...prev, isAutoRotating: !prev.isAutoRotating }))}
                      className={`w-full flex items-center justify-center gap-1 px-2 py-1 rounded text-xs transition-colors mb-2 ${
                        graphState.isAutoRotating 
                          ? 'bg-indigo-600 text-white' 
                          : 'bg-indigo-100 hover:bg-indigo-200 text-indigo-700'
                      }`}
                    >
                      {graphState.isAutoRotating ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                      {graphState.isAutoRotating ? 'Stop Auto Rotate' : 'Auto Rotate'}
                    </button>

                    {/* Zoom Controls */}
                    <div className="flex items-center gap-1 mb-2">
                      <button
                        onClick={() => setGraphState(prev => ({ ...prev, zoom: Math.min(prev.zoom + 0.2, 3) }))}
                        className="p-1 bg-indigo-100 hover:bg-indigo-200 rounded transition-colors"
                      >
                        <ZoomIn className="w-3 h-3 text-indigo-700" />
                      </button>
                      <span className="text-xs text-indigo-600 flex-1 text-center min-w-12 font-medium">
                        {Math.round(graphState.zoom * 100)}%
                      </span>
                      <button
                        onClick={() => setGraphState(prev => ({ ...prev, zoom: Math.max(prev.zoom - 0.2, 0.3) }))}
                        className="p-1 bg-indigo-100 hover:bg-indigo-200 rounded transition-colors"
                      >
                        <ZoomOut className="w-3 h-3 text-indigo-700" />
                      </button>
                    </div>

                    {/* Reset View */}
                    <button
                      onClick={resetView}
                      className="w-full flex items-center justify-center gap-1 px-2 py-1 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded transition-colors text-xs font-medium"
                    >
                      <RotateCcw className="w-3 h-3" />
                      Reset All
                    </button>
                  </div>

                  {/* 3D Rotation Controls */}
                  <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-10">
                    <button
                      onClick={() => rotateGraph('x', -15)}
                      className="p-2 bg-white/90 hover:bg-white shadow-lg rounded-lg transition-all duration-200 hover:scale-110 border border-indigo-200"
                      title="Rotate Up (X-axis -15°)"
                    >
                      <ChevronUp className="w-5 h-5 text-indigo-700" />
                    </button>
                  </div>

                  <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 z-10">
                    <button
                      onClick={() => rotateGraph('x', 15)}
                      className="p-2 bg-white/90 hover:bg-white shadow-lg rounded-lg transition-all duration-200 hover:scale-110 border border-indigo-200"
                      title="Rotate Down (X-axis +15°)"
                    >
                      <ChevronDown className="w-5 h-5 text-indigo-700" />
                    </button>
                  </div>

                  <div className="absolute left-2 top-1/2 transform -translate-y-1/2 z-10">
                    <button
                      onClick={() => rotateGraph('y', -15)}
                      className="p-2 bg-white/90 hover:bg-white shadow-lg rounded-lg transition-all duration-200 hover:scale-110 border border-indigo-200"
                      title="Rotate Left (Y-axis -15°)"
                    >
                      <ChevronLeft className="w-5 h-5 text-indigo-700" />
                    </button>
                  </div>

                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2 z-10">
                    <button
                      onClick={() => rotateGraph('y', 15)}
                      className="p-2 bg-white/90 hover:bg-white shadow-lg rounded-lg transition-all duration-200 hover:scale-110 border border-indigo-200"
                      title="Rotate Right (Y-axis +15°)"
                    >
                      <ChevronRight className="w-5 h-5 text-indigo-700" />
                    </button>
                  </div>

                  <div className="absolute top-2 right-2 z-10 flex gap-1">
                    <button
                      onClick={() => rotateGraph('z', -15)}
                      className="p-2 bg-white/90 hover:bg-white shadow-lg rounded-lg transition-all duration-200 hover:scale-110 border border-indigo-200"
                      title="Rotate Counter-clockwise (Z-axis -15°)"
                    >
                      <RotateCcw className="w-4 h-4 text-indigo-700" />
                    </button>
                    <button
                      onClick={() => rotateGraph('z', 15)}
                      className="p-2 bg-white/90 hover:bg-white shadow-lg rounded-lg transition-all duration-200 hover:scale-110 border border-indigo-200"
                      title="Rotate Clockwise (Z-axis +15°)"
                    >
                      <RotateCw className="w-4 h-4 text-indigo-700" />
                    </button>
                  </div>

                  {/* Enhanced SVG Canvas with touch support */}
                  <svg
                    ref={svgRef}
                    width="100%"
                    height="600"
                    viewBox="0 0 800 600"
                    className="cursor-grab active:cursor-grabbing"
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onWheel={handleWheel}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    style={{ touchAction: 'none' }} // Prevent default touch behaviors
                  >
                    {(() => {
                      try {
                        return renderProfessionalGraph();
                      } catch (error) {
                        console.error('MathsSolver: Error rendering SVG', error);
                        return (
                          <g>
                            <text x="400" y="300" textAnchor="middle" fill="#666" fontSize="16">
                              Error rendering graph. Please try again.
                            </text>
                          </g>
                        );
                      }
                    })()}
                  </svg>

                  {/* Enhanced Legend */}
                  <div className="absolute top-4 right-4 bg-white/95 rounded-lg p-3 text-xs border border-indigo-200">
                    <div className="font-semibold text-indigo-800 mb-2">Entity Types</div>
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2">
                        <span>🧠</span>
                        <span className="text-blue-600">Concepts</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span>📐</span>
                        <span className="text-emerald-600">Formulas</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span>🔢</span>
                        <span className="text-amber-600">Values</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span>✅</span>
                        <span className="text-violet-600">Steps</span>
                      </div>
                    </div>
                    {/* Touch controls hint for tablet users */}
                    <div className="mt-3 pt-2 border-t border-indigo-200">
                      <div className="text-xs text-indigo-600 font-medium">Touch Controls:</div>
                      <div className="text-xs text-indigo-500">• Single touch: Pan</div>
                      <div className="text-xs text-indigo-500">• Two fingers: Zoom</div>
                    </div>
                  </div>
                </div>

                {/* Enhanced Status Display */}
                <div className="mt-4 flex items-center justify-center gap-6 text-sm text-indigo-600 font-medium">
                  <span>X: {graphState.rotation.x}°</span>
                  <span>Y: {graphState.rotation.y}°</span>
                  <span>Z: {graphState.rotation.z}°</span>
                  <span>Zoom: {Math.round(graphState.zoom * 100)}%</span>
                  {solution && <span>Entities: {solution?.graphData?.nodes?.length || 0}</span>}
                  {graphState.isAutoRotating && (
                    <span className="flex items-center gap-1 text-indigo-600">
                      <RotateCw className="w-4 h-4 animate-spin" />
                      Auto-rotating
                    </span>
                  )}
                  {/* Touch status indicator */}
                  {graphState.isTouching && (
                    <span className="flex items-center gap-1 text-green-600">
                      <Hand className="w-4 h-4" />
                      Touch Mode
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Enhanced Analysis/List View Mode */
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Search and Summary */}
            <div className="lg:col-span-3 bg-white rounded-xl shadow-lg p-6 border border-indigo-200">
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-between mb-6">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search mathematical entities..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border-2 border-indigo-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                
                                  {solution && solution.graphData && (
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-indigo-600 font-medium">Found: {filteredEntities.length} entities</span>
                      <button
                        onClick={() => setFloatingWindow(prev => ({ ...prev, isVisible: true }))}
                        className="px-3 py-2 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-lg transition-colors flex items-center gap-2"
                      >
                        <Info className="w-4 h-4" />
                        Analysis Panel
                      </button>
                    </div>
                  )}
              </div>

              {/* Quick Solution Summary */}
              {solution && solution.equation && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-800">Original Equation</h4>
                    <MathExpression className="text-blue-700">{solution.equation || 'No equation'}</MathExpression>
                  </div>
                  <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <h4 className="font-semibold text-emerald-800">Discriminant</h4>
                    <MathExpression className="text-emerald-700">Δ = {solution.discriminant || 'N/A'}</MathExpression>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <h4 className="font-semibold text-amber-800">Nature</h4>
                    <p className="text-amber-700 text-sm">{solution.nature || 'N/A'}</p>
                  </div>
                  <div className="p-4 bg-violet-50 rounded-lg border border-violet-200">
                    <h4 className="font-semibold text-violet-800">Solutions</h4>
                    <div className="text-violet-700 text-sm">
                      <div>x₁ = {solution.solutions?.x1 !== undefined ? (typeof solution.solutions.x1 === 'number' ? solution.solutions.x1.toFixed(3) : solution.solutions.x1) : 'N/A'}</div>
                      <div>x₂ = {solution.solutions?.x2 !== undefined ? (typeof solution.solutions.x2 === 'number' ? solution.solutions.x2.toFixed(3) : solution.solutions.x2) : 'N/A'}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Entity Categories */}
            <div className="lg:col-span-2 space-y-6">
              {[
                { 
                  key: 'concepts', 
                  label: 'Mathematical Concepts',
                  icon: Brain,
                  color: 'blue',
                  description: 'Core mathematical ideas and principles'
                },
                { 
                  key: 'formulas', 
                  label: 'Formulas & Methods',
                  icon: Calculator, 
                  color: 'emerald',
                  description: 'Mathematical formulas and solution methods'
                },
                { 
                  key: 'values', 
                  label: 'Values & Results',
                  icon: Star, 
                  color: 'amber',
                  description: 'Numerical values and computed results'
                },
                { 
                  key: 'steps', 
                  label: 'Solution Steps',
                  icon: BookOpen, 
                  color: 'violet',
                  description: 'Step-by-step solution process'
                }
              ].map(category => {
                const categoryEntities = filteredEntities.filter(entity => 
                  entity.type === category.key
                );

                if (categoryEntities.length === 0 && !solution) return null;

                const Icon = category.icon;

                return (
                  <div 
                    key={category.key} 
                    className={`bg-white rounded-xl shadow-lg p-6 border border-${category.color}-200 transition-all duration-300`}
                  >
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <Icon className={`w-5 h-5 text-${category.color}-600`} />
                      {category.label}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">{category.description}</p>
                    
                    {categoryEntities.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {categoryEntities.map((entity) => (
                          <div
                            key={entity.id}
                            onClick={() => handleEntityClick(entity, { stopPropagation: () => {} })}
                            className={`p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md hover:scale-105 active:scale-95 ${
                              graphState.selectedNode === entity.id
                                ? 'ring-2 ring-indigo-500 ring-offset-2 border-indigo-200 bg-indigo-50'
                                : 'border-gray-200 hover:border-indigo-300 bg-white'
                            }`}
                          >
                            <h4 className="font-semibold text-sm text-gray-800">{entity.name}</h4>
                            <p className="text-xs mt-1 text-gray-600">{entity.description}</p>
                            <div className="mt-2">
                              <MathExpression className="text-xs text-indigo-700 bg-indigo-50 px-2 py-1 rounded">
                                {entity.value}
                              </MathExpression>
                            </div>
                            <div className="flex items-center justify-between mt-2">
                              <span className={`inline-block px-2 py-1 text-xs rounded-full bg-${category.color}-100 text-${category.color}-800 font-medium`}>
                                {entity.type}
                              </span>
                              <div className="flex items-center gap-1">
                                {[...Array(5)].map((_, i) => (
                                  <Star 
                                    key={i} 
                                    className={`w-3 h-3 ${i < entity.importance / 2 ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Icon className="w-12 h-12 mx-auto mb-2 opacity-30" />
                        <p>No {category.label.toLowerCase()} available</p>
                        <p className="text-xs mt-1">Solve an equation to see mathematical entities</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Enhanced Entity Analysis Panel */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-indigo-200">
              <h3 className="text-xl font-semibold text-indigo-900 mb-4 flex items-center gap-2">
                <Info className="w-5 h-5" />
                {graphState.selectedNode && solution ? 'Detailed Analysis' : 'Select an Entity'}
              </h3>
              
              {graphState.selectedNode && solution ? (
                <div className="space-y-4">
                  {(() => {
                    const selectedEntity = solution?.graphData?.nodes?.find(n => n.id === graphState.selectedNode);
                    if (!selectedEntity) return null;

                    return (
                      <>
                        <div className="p-4 rounded-lg border border-indigo-200 bg-indigo-50">
                          <h4 className="font-bold text-lg text-indigo-900">{selectedEntity.name}</h4>
                          <p className="text-sm text-indigo-700 mt-1">{selectedEntity.description}</p>
                          <div className="mt-3">
                            <MathExpression className="text-indigo-800 bg-indigo-100 px-3 py-2 rounded">
                              {selectedEntity.value}
                            </MathExpression>
                          </div>
                          <div className="flex items-center justify-between mt-3">
                            <span className="inline-block px-2 py-1 text-xs rounded-full bg-indigo-200 text-indigo-800 font-medium">
                              {selectedEntity.type}
                            </span>
                            <div className="flex items-center gap-1">
                              {[...Array(5)].map((_, i) => (
                                <Star 
                                  key={i} 
                                  className={`w-3 h-3 ${i < selectedEntity.importance / 2 ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
                                />
                              ))}
                            </div>
                          </div>
                        </div>

                        <div>
                          <h4 className="font-semibold text-indigo-900 mb-3 flex items-center gap-2">
                            <Network className="w-4 h-4" />
                            Mathematical Relationships
                          </h4>
                          <div className="space-y-2 max-h-64 overflow-y-auto">
                            {solution?.graphData?.links
                              ?.filter(link => {
                                const sourceId = link.source || link.from;
                                const targetId = link.target || link.to;
                                return sourceId === selectedEntity.id || targetId === selectedEntity.id;
                              })
                              ?.map((relation, index) => {
                                const sourceId = relation.source || relation.from;
                                const targetId = relation.target || relation.to;
                                const relatedId = sourceId === selectedEntity.id ? targetId : sourceId;
                                const relatedEntity = solution?.graphData?.nodes?.find(e => e.id === relatedId);
                                const isOutgoing = sourceId === selectedEntity.id;

                                return (
                                  <div 
                                    key={index} 
                                    className="p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-all duration-300 cursor-pointer"
                                    onClick={() => setGraphState(prev => ({ ...prev, selectedNode: relatedId }))}
                                  >
                                    <div className="flex items-center gap-2 text-sm">
                                      <span className="font-medium text-indigo-900">
                                        {isOutgoing ? selectedEntity.name : relatedEntity?.name}
                                      </span>
                                      <span className="text-indigo-600 italic font-medium">{relation.type}</span>
                                      <span className="font-medium text-indigo-900">
                                        {isOutgoing ? relatedEntity?.name : selectedEntity.name}
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-between mt-2">
                                      <p className="text-xs text-gray-600">{relatedEntity?.description}</p>
                                      <span className="text-xs text-indigo-500 font-medium">
                                        Strength: {relation.strength}/10
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              ) : (
                <div className="text-center text-indigo-600 py-8">
                  <Brain className="w-12 h-12 mx-auto mb-3 opacity-60" />
                  <p className="font-medium">Mathematical Entity Analysis</p>
                  <p className="text-sm mt-1">Click on any entity to view detailed analysis and relationships</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Render different pages based on currentPage */}
        {currentPage === 'graph-view' && (
          <GraphViewPage 
            solution={solution} 
            onBack={handleBackToSolver}
          />
        )}

        {currentPage === 'analysis-view' && (
          <AnalysisViewPage 
            solution={solution} 
            onBack={handleBackToSolver}
          />
        )}

        {currentPage === 'toolbox' && (
          <ToolBoxPage 
            solution={solution} 
            onRun={handleToolBoxRun} 
            onBack={handleBackToSolver}
          />
        )}

        {currentPage === 'ai-generated' && (
          <AIGeneratedPage 
            workspaceData={workspaceData} 
            solution={solution} 
            onBack={handleBackToSolver}
          />
        )}

        {/* Main Solver View - Problem Input and Basic Info */}
        {currentPage === 'solver' && (
          <div className="space-y-6">
            {/* Problem Input Section */}
            <div className="bg-white rounded-xl shadow-lg border border-indigo-200 p-6">
              <h3 className="text-lg font-semibold text-indigo-900 mb-4">Problem Solver</h3>
              <p className="text-gray-600 mb-4">
                Enter your mathematical problem and use the tabs above to access different views:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <Network className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                  <h4 className="font-semibold text-blue-900 text-sm">Graph View</h4>
                  <p className="text-blue-700 text-xs">Interactive knowledge graph</p>
                </div>
                <div className="text-center p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                  <Brain className="w-8 h-8 mx-auto mb-2 text-indigo-600" />
                  <h4 className="font-semibold text-indigo-900 text-sm">Analysis View</h4>
                  <p className="text-indigo-700 text-xs">Detailed problem analysis</p>
                </div>
                <div className="text-center p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <Wrench className="w-8 h-8 mx-auto mb-2 text-emerald-600" />
                  <h4 className="font-semibold text-emerald-900 text-sm">ToolBox</h4>
                  <p className="text-emerald-700 text-xs">Mathematical workspace</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <Sparkles className="w-8 h-8 mx-auto mb-2 text-purple-600" />
                  <h4 className="font-semibold text-purple-900 text-sm">AI Generated</h4>
                  <p className="text-purple-700 text-xs">AI-powered insights</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* COMPLETELY FIXED: Ultra-stable floating window */}
        <FloatingEntityWindow />

        {/* Enhanced Statistics Dashboard - Only show on solver page to avoid overlap */}
        {currentPage === 'solver' && solution && solution.graphData && (
          <div className="mt-8 mb-20 md:mb-24 bg-white rounded-xl shadow-lg p-6 border border-indigo-200 relative z-10" data-statistics-section style={{ minHeight: '200px' }}>
            <h3 className="text-xl font-semibold text-indigo-900 mb-4 flex items-center gap-2">
              <Star className="w-5 h-5" />
              Mathematical Knowledge Graph Statistics
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-2xl font-bold text-blue-600">
                  {solution?.graphData?.nodes?.filter(n => n && n.type === 'concepts')?.length || 0}
                </div>
                <div className="text-sm text-blue-700">Concepts</div>
              </div>
              <div className="text-center p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <div className="text-2xl font-bold text-emerald-600">
                  {solution?.graphData?.nodes?.filter(n => n && n.type === 'formulas')?.length || 0}
                </div>
                <div className="text-sm text-emerald-700">Formulas</div>
              </div>
              <div className="text-center p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="text-2xl font-bold text-amber-600">
                  {solution?.graphData?.nodes?.filter(n => n && n.type === 'values')?.length || 0}
                </div>
                <div className="text-sm text-amber-700">Values</div>
              </div>
              <div className="text-center p-4 bg-violet-50 rounded-lg border border-violet-200">
                <div className="text-2xl font-bold text-violet-600">
                  {solution?.graphData?.nodes?.filter(n => n && n.type === 'steps')?.length || 0}
                </div>
                <div className="text-sm text-violet-700">Steps</div>
              </div>
              <div className="text-center p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                <div className="text-2xl font-bold text-indigo-600">{solution?.graphData?.links?.length || 0}</div>
                <div className="text-sm text-indigo-700">Relationships</div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="text-2xl font-bold text-purple-600">
                  {solution?.graphData?.nodes?.length ? Math.round(solution.graphData.nodes.filter(n => n && n.importance).reduce((sum, n) => sum + (n.importance || 0), 0) / solution.graphData.nodes.filter(n => n && n.importance).length) : 0}
                </div>
                <div className="text-sm text-purple-700">Avg Importance</div>
              </div>
            </div>
          </div>
        )}

        {/* Think Progress Modal */}
        <ThinkProgressModal
          isOpen={showThinkProgress}
          progress={thinkProgress}
          onClose={() => {
            setShowThinkProgress(false);
            setThinkProgress(null);
          }}
        />
      </div>
    </div>
  );
}

export default MathsSolver;